import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ActivityIndicator,
  Platform,
  ScrollView,
  TouchableOpacity,
  Alert,
  TextInput,
  FlatList,
} from 'react-native';
import { overlayBackdropStyle } from '../components/OverlayPanel';
import { InsetModal as Modal } from '../components/InsetModal';
import { Ionicons } from '@expo/vector-icons';
import { DirectionalIcon } from '../components/DirectionalIcon';
import * as Clipboard from 'expo-clipboard';
import { ScreenLayout, Header, FormHeader, Input, Button, ContactSelector, Dropdown, NotesSection, ConfirmationModal } from '../components';
import { PackingSlipModal } from '../components/vendors/PackingSlipModal';
import { DocumentModal } from '../components/documents/DocumentModal';
import { ORDER_DOCUMENT_TYPES } from '../utils/documentTypes';
import type { ContactSelectorMode, ContactSelectorVariant, Note } from '../components';
import { MapLocationPicker } from '../components/MapLocationPicker';
import { ProductSelectorModal } from '../components/catalogues/ProductSelectorModal';
import { DataListFieldRenderer } from '../components/data-lists/DataListFieldRenderer';
import { apiService, DataListColumn } from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { useAppStore } from '../store/appStore';
import { colors, spacing, typography, withAlpha } from '../theme';
import { designTokens } from '../theme/design-system';
import { convertAmount, getExchangeRate } from '../utils/currencyConverter';
import { CurrencyPicker } from '../components/design-system';
import { usePermissions } from '../hooks/usePermissions';
import { useThemedRTLStyles } from '../hooks/useThemedRTLStyles';
import { usePhoneFormGutter } from '../hooks/usePhoneFormGutter';
import { useLocale } from '../contexts/LocaleContext';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { useOrderMinimums } from '../hooks/useOrderMinimums';
import { OrderMinimumsNotice } from '../components/OrderMinimumsNotice';
import { businessCurrency } from '../utils/businessDefaults';

interface ProductAddon {
  id: string;
  name: string;
  price: number;
  currency?: string;
  description?: string;
  isRequired?: boolean;
  maxQuantity?: number;
}

interface SelectedAddon {
  addonId: string;
  name: string;
  price: number;
  currency?: string;
  quantity: number;
}

interface OrderItem {
  productId: string;
  variantId?: string;
  quantity: number;
  product: {
    _id: string;
    name: string;
    price: number;
    salePrice?: number;
    currency?: string;
    primaryImage?: string;
    brand?: string;
    sku?: string;
    productType?: 'physical' | 'digital' | 'service';
    addons?: ProductAddon[];
    priceUnit?: string;
    unitId?: string;
    inventoryTracking?: boolean;
    inventoryQuantity?: number;
    /** The product's vendors (first = primary) — for the line's vendor picker; not saved on the order. */
    vendorIds?: string[];
  };
  variant?: {
    id: string;
    name: string;
    price?: number;
  };
  selectedAddons?: SelectedAddon[];
  unitPrice: number;
  totalPrice: number;
  /** The vendor supplying this line (defaults to the product's primary vendor). */
  vendorId?: string;
  vendorName?: string;
}

interface OrderFormData {
  customer: {
    name: string;
    phone: string;
    email?: string;
    address?: string;
    location?: {
      latitude?: number;
      longitude?: number;
      address?: string;
      text?: string;
      city?: string;
      country?: string;
    };
  };
  items: OrderItem[];
  orderStatus: 'pending' | 'placed' | 'confirmed' | 'processing' | 'shipped' | 'delivered' | 'cancelled';
  paymentStatus?: 'unpaid' | 'partial' | 'paid' | 'refunded';
  shippingAddress?: string;
  shippingLocation?: {
    latitude: number;
    longitude: number;
    address: string;
    text?: string;
    city?: string;
    country?: string;
  };
  shippingMethod?: string;
  shippingMethodId?: string;
  shippingMethodSnapshot?: { name: string; type: string; price: number; providerName?: string };
  trackingNumber?: string;
  notes?: string | Note[]; // Support both string (legacy) and array (new)
  subtotal: number;
  tax?: number;
  extraCharges?: { name: string; amount: number; _optional?: boolean }[];
  shipping?: number;
  discount?: number;
  manualDiscount?: number;       // agent-entered manual discount
  discountReason?: string;
  totalPrice: number;
  currency: string;
}

interface AddOrderScreenProps {
  mode: 'new' | 'edit';
  orderId?: string;        // Required for 'edit' mode
}

/**
 * Unified Add/Edit Order Screen
 * 
 * Handles both creating new orders and editing existing orders
 * based on the `mode` prop:
 * - 'new': Creates a new order
 * - 'edit': Edits existing order (requires orderId)
 */
export const AddOrderScreen: React.FC<AddOrderScreenProps> = ({
  mode,
  orderId,
}) => {
  const styles = useThemedRTLStyles(getStyles);
  const phoneFormGutter = usePhoneFormGutter();
  const { t } = useLocale();
  const rtl = useRTLStyles();
  const { navigateTo, showNotification, routeParams, activeTab, setActiveTab } = useAppContext();
  const { currentBusiness, user } = useAppStore();
  const { hasPermissionAtLeast } = usePermissions();
  // A user without contacts.manage must never write to a contact's core
  // fields (phone/email/identifiers/etc.) from this screen — confirmed real
  // production impact: a user with only contacts.manageNotes-level access
  // saw a MASKED phone number (their contacts.viewMobileNumber level hides
  // it, e.g. "+*******6631") surfaced through the contact picker here, and
  // this screen's own phone-changed-on-save detection below could silently
  // write that masked string back as the contact's real phone number. The
  // backend now enforces this too (OmniContactController.update), but
  // gating here as well avoids a pointless doomed request and a confusing
  // 403 for a user who was never going to be allowed to edit the contact.
  const canManageContacts = hasPermissionAtLeast('contacts.manage', 'edit');
  const listId: string | undefined = routeParams?.listId;
  const [loading, setLoading] = useState(false);
  const [loadingOrder, setLoadingOrder] = useState(mode === 'edit');

  // Extra fields (item 2) — custom columns on the linked system Orders Data
  // List, alongside this order's own fixed fields. Only meaningful in edit
  // mode: a brand-new order has no real record id yet for values to attach
  // to (mirrors the same create-vs-edit constraint DataListFieldRenderer's
  // own recordId prop already documents for media uploads).
  const [customColumns, setCustomColumns] = useState<DataListColumn[]>([]);
  const [customValues, setCustomValues] = useState<Record<string, any>>({});
  const [customValueErrors, setCustomValueErrors] = useState<Record<string, string>>({});
  const [savingCustomValues, setSavingCustomValues] = useState(false);

  useEffect(() => {
    if (!listId || mode !== 'edit' || !orderId) return;
    apiService.getDataList(listId).then((res) => {
      if (res.statusCode === 1000 && res.data?.customColumns?.length) {
        setCustomColumns(res.data.customColumns);
      }
    }).catch((err) => console.error('[AddOrderScreen] Failed to load linked list:', err));
    apiService.getDataListRecord(listId, orderId).then((res) => {
      if (res.statusCode === 1000 && res.data) {
        setCustomValues(res.data.values || {});
      }
    }).catch((err) => console.error('[AddOrderScreen] Failed to load custom field values:', err));
  }, [listId, mode, orderId]);

  const handleSaveCustomValues = async () => {
    if (!listId || !orderId) return;
    try {
      setSavingCustomValues(true);
      const res = await apiService.upsertDataListRecordCustomValues(listId, orderId, customValues);
      if (res.statusCode === 1000) {
        showNotification('success', t('orders.common.success'), t('orders.form.extraFieldsUpdated'));
        setCustomValueErrors({});
      } else {
        showNotification('error', t('orders.common.error'), res.errors?.[0]?.message || t('orders.form.extraFieldsFailed'));
      }
    } catch (error: any) {
      showNotification('error', t('orders.common.error'), error.message || t('orders.form.extraFieldsFailed'));
    } finally {
      setSavingCustomValues(false);
    }
  };
  
  // Contact selection state
  const [selectedContact, setSelectedContact] = useState<any | null>(null);
  const [originalContactId, setOriginalContactId] = useState<string | null>(null); // Track original contact ID for edit mode
  // Map of productId → original quantity when the order was loaded (edit mode)
  const originalItemQtyRef = React.useRef<Map<string, number>>(new Map());
  // Stock warning modal
  const [stockWarning, setStockWarning] = useState<{ message: string; resolve: (v: boolean) => void } | null>(null);
  const [orderNumber, setOrderNumber] = useState<string>('');
  const [quotationId, setQuotationId] = useState<string | undefined>(undefined);

  const [formData, setFormData] = useState<OrderFormData>({
    customer: {
      name: '',
      phone: '',
      email: '',
      address: '',
    },
    items: [],
    orderStatus: 'pending',
    paymentStatus: 'unpaid',
    shippingAddress: '',
    shippingMethod: '',
    trackingNumber: '',
    notes: '',
    subtotal: 0,
    tax: 0,
    extraCharges: [],
    shipping: 0,
    discount: 0,
    manualDiscount: 0,
    discountReason: '',
    totalPrice: 0,
    currency: currentBusiness?.currency || 'USD',
  });

  // Product minimum quantities + vendor minimum order amounts — the server
  // rejects the order otherwise, so show them while editing items.
  const minimumsLines = useMemo(
    () => formData.items.map((i) => ({ productId: i.productId, variantId: i.variantId, quantity: i.quantity, lineTotal: Number((i as any).totalPrice) || 0 })),
    [formData.items]
  );
  const { report: minimums, ok: minimumsOk } = useOrderMinimums(currentBusiness?._id, minimumsLines, formData.currency);

  // Order currency — defaults to the business currency configured in
  // Business Settings (the same one the server falls back to), and can be
  // changed per order. Product prices are converted into it when added.
  const orderCurrencyRef = useRef(formData.currency);
  orderCurrencyRef.current = formData.currency;
  const currencyTouchedRef = useRef(false);
  const [changingCurrency, setChangingCurrency] = useState(false);
  useEffect(() => {
    if (mode !== 'new') return;
    apiService.getBusinessConfig()
      .then((res) => {
        const configured = (res.statusCode === 1000 && res.data?.currency) || currentBusiness?.currency;
        if (!configured || currencyTouchedRef.current) return;
        setFormData((prev) => (prev.items.length === 0 ? { ...prev, currency: String(configured).toUpperCase() } : prev));
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, currentBusiness?._id]);

  /** Switch the order to another currency, converting everything already
   * priced (items, add-ons, shipping, discounts) at the live rate. */
  const handleCurrencyChange = async (next: string) => {
    const from = formData.currency;
    if (!next || next === from) return;
    currencyTouchedRef.current = true;
    setChangingCurrency(true);
    try {
      const rate = await getExchangeRate(from, next);
      const conv = (v?: number) => (typeof v === 'number' ? Math.round(v * rate * 100) / 100 : v);
      const items = (formData.items as OrderItem[]).map((item: any) => {
        const unitPrice = conv(item.unitPrice) as number;
        return {
          ...item,
          unitPrice,
          totalPrice: Math.round(unitPrice * item.quantity * 100) / 100,
          product: {
            ...item.product,
            price: conv(item.product?.price),
            salePrice: conv(item.product?.salePrice),
            currency: next,
            addons: (item.product?.addons || []).map((a: any) => ({ ...a, price: conv(a.price), currency: next })),
          },
          variant: item.variant ? { ...item.variant, price: conv(item.variant.price) } : item.variant,
          selectedAddons: (item.selectedAddons || []).map((a: any) => ({ ...a, price: conv(a.price), currency: next })),
        };
      });
      setFormData((prev) => ({
        ...prev,
        currency: next,
        items,
        shipping: conv(prev.shipping) as number,
        discount: conv(prev.discount) as number,
        manualDiscount: conv(prev.manualDiscount) as number,
        shippingMethodSnapshot: prev.shippingMethodSnapshot
          ? { ...prev.shippingMethodSnapshot, price: conv(prev.shippingMethodSnapshot.price) as number }
          : prev.shippingMethodSnapshot,
      }));
      if (items.length > 0) await recalculateTotals(items);
    } finally {
      setChangingCurrency(false);
    }
  };

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [orderStatusDropdownOpen, setOrderStatusDropdownOpen] = useState(false);
  const [paymentStatusDropdownOpen, setPaymentStatusDropdownOpen] = useState(false);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [showProductSelector, setShowProductSelector] = useState(false);
  const [addingProducts, setAddingProducts] = useState(false);
  const [showShippingMethodModal, setShowShippingMethodModal] = useState(false);
  const [shippingMethods, setShippingMethods] = useState<any[]>([]);
  const [loadingShippingMethods, setLoadingShippingMethods] = useState(false);
  const [selectedShippingMethod, setSelectedShippingMethod] = useState<any | null>(null);
  const [expressSelected, setExpressSelected] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState<{ code: string; discountAmount: number } | null>(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [loadingCharges, setLoadingCharges] = useState(false);
  const [optionalCharges, setOptionalCharges] = useState<{ name: string; amount: number; checked: boolean }[]>([]);

  const [scannedProductInjected, setScannedProductInjected] = useState(false);
  const orderLocationPrefillAppliedRef = useRef(false);

  // Load order data for edit mode
  useEffect(() => {
    if (mode === 'edit' && orderId) {
      loadOrder();
    }
  }, [mode, orderId]);




  const loadShippingMethods = async () => {
    try {
      setLoadingShippingMethods(true);
      const response = await apiService.getShippingMethods({ isActive: true, limit: 50 });
      if (response.statusCode === 1000 && response.data) {
        const methods = Array.isArray(response.data.methods) ? response.data.methods : Array.isArray(response.data) ? response.data : [];
        setShippingMethods(methods);
      }
    } catch (error) {
      console.error('Failed to load shipping methods:', error);
    } finally {
      setLoadingShippingMethods(false);
    }
  };

  const toggleOptionalCharge = (name: string, checked: boolean, amount: number) => {
    setOptionalCharges(prev => prev.map(c => c.name === name ? { ...c, checked } : c));
    setFormData(prev => {
      let extraCharges = prev.extraCharges ?? [];
      if (checked) {
        // Add if not already present
        if (!extraCharges.some((c: any) => c.name === name && c._optional)) {
          extraCharges = [...extraCharges, { name, amount, _optional: true }];
        }
      } else {
        extraCharges = extraCharges.filter((c: any) => !(c.name === name && c._optional));
      }
      const chargesTotal = extraCharges.reduce((s: number, c: any) => s + c.amount, 0);
      const totalPrice = Math.max(0, prev.subtotal + (prev.tax || 0) + (prev.shipping || 0) + chargesTotal - (prev.discount || 0));
      return { ...prev, extraCharges, totalPrice };
    });
  };

  const applyDiscount = (discountAmount: number) => {
    setFormData(prev => {
      const chargesTotal = (prev.extraCharges ?? []).reduce((s, c) => s + c.amount, 0);
      const totalPrice = Math.max(0, prev.subtotal + (prev.tax || 0) + (prev.shipping || 0) + chargesTotal - discountAmount);
      return { ...prev, discount: discountAmount, totalPrice };
    });
  };

  // Returns the best available delivery location for zone-based pricing.
  // Prefers the explicitly map-picked shipping location; falls back to the contact's location.
  const getDeliveryLocation = () => {
    const sl = formData.shippingLocation;
    if (sl?.latitude !== undefined && sl?.longitude !== undefined) {
      return { latitude: sl.latitude, longitude: sl.longitude };
    }
    const cl = selectedContact?.location;
    if (cl?.latitude !== undefined && cl?.longitude !== undefined) {
      return { latitude: cl.latitude, longitude: cl.longitude };
    }
    return undefined;
  };

  const applyCouponCode = async () => {
    if (!couponCode.trim()) return;
    if (!currentBusiness?._id) { showNotification('warning', t('orders.common.error'), t('orders.form.businessNotFound')); return; }
    setValidatingCoupon(true);
    try {
      const orderItems = formData.items
        .filter(i => i.productId)
        .map(i => ({ productId: i.productId, totalPrice: i.totalPrice }));
      const res = await apiService.validateCoupon(
        currentBusiness._id,
        couponCode.trim(),
        formData.subtotal,
        selectedContact?._id,
        orderItems.length ? orderItems : undefined,
        getDeliveryLocation(),
      );
      if (res.statusCode === 1000 && res.data?.valid) {
        const discount = res.data.discountAmount;
        setAppliedCoupon({ code: couponCode.trim().toUpperCase(), discountAmount: discount });
        const totalDiscount = discount;
        applyDiscount(totalDiscount);
        showNotification('success', t('orders.form.couponApplied'), t('orders.form.couponSaved', { currency: formData.currency, amount: discount.toFixed(2) }));
      } else {
        showNotification('warning', t('orders.form.couponInvalid'), res.data?.reason || t('orders.form.couponInvalidMsg'));
      }
    } catch (e: any) {
      showNotification('error', t('orders.common.error'), e.message);
    } finally {
      setValidatingCoupon(false);
    }
  };


  const calcShippingPrice = (method: any, subtotal: number, useExpress: boolean): number => {
    let base = method.price ?? 0;
    // Free shipping with min order threshold: charge belowMinCost when not yet reached
    if (method.type === 'free' && method.minOrderAmount > 0 && subtotal < method.minOrderAmount) {
      // belowMinCost must be explicitly configured; if missing, default to 0 but flag via isFreeThresholdNotMet
      base = method.belowMinCost != null ? method.belowMinCost : 0;
    }
    // location_based: base price is already resolved via resolveZonePrice; stored in method._resolvedPrice
    if (method.type === 'location_based') {
      base = method._resolvedPrice ?? method.price ?? 0;
    }
    const extra = useExpress && method.expressOption?.enabled ? (method.expressOption.extraCost ?? 0) : 0;
    return base + extra;
  };

  const isFreeThresholdNotMet = (method: any | null, subtotal: number): boolean => {
    return !!method && method.type === 'free' && method.minOrderAmount > 0 && subtotal < method.minOrderAmount;
  };

  /**
   * For location_based methods: call the resolve-zone endpoint and return the
   * enriched method object with `_resolvedPrice` and `_resolvedZone` attached.
   * Falls back gracefully if the call fails.
   */
  const resolveZoneForMethod = async (
    method: any,
    customer: {
      address?: string;
      location?: {
        latitude?: number;
        longitude?: number;
        address?: string;
        text?: string;
        city?: string;
        country?: string;
      };
    }
  ): Promise<any> => {
    if (method.type !== 'location_based') return method;
    try {
      // Rely on GPS coordinates for zone matching (polygon / haversine on the backend).
      const res = await apiService.resolveShippingZone(method._id || method.id, {
        latitude: customer.location?.latitude,
        longitude: customer.location?.longitude,
      });
      if (res.statusCode === 1000 && res.data) {
        return {
          ...method,
          _resolvedPrice: res.data.price,
          _resolvedZone: res.data.zone,
          _zoneMatched: res.data.matched,
          _zoneFallback: res.data.fallback,
        };
      }
    } catch (_) {/* non-fatal */}
    return method;
  };

  const applyShippingMethod = async (method: any, useExpress: boolean) => {
    // For location_based, resolve zone price against current customer location
    let resolvedMethod = method;
    if (method.type === 'location_based') {
      // Prefer the explicit shipping location (picked on map) over the customer profile location
      const shippingLoc = formData.shippingLocation;
      const customer = {
        address: shippingLoc?.address || formData.shippingAddress || formData.customer.address,
        location: shippingLoc
          ? { latitude: shippingLoc.latitude, longitude: shippingLoc.longitude, address: shippingLoc.address }
          : formData.customer.location,
      };
      resolvedMethod = await resolveZoneForMethod(method, customer);
      if (!resolvedMethod._zoneMatched) {
        showNotification('warning', t('orders.form.zoneUnavailable'), t('orders.form.zoneUnavailableCustomer'));
        return;
      }
    }

    setFormData(prev => {
      const subtotal = prev.subtotal || 0;
      const price = calcShippingPrice(resolvedMethod, subtotal, useExpress);
      const tax = prev.tax || 0;
      const discount = prev.discount || 0;
      const totalPrice = subtotal + tax + price - discount;
      const zoneSuffix = resolvedMethod._resolvedZone ? ` · ${resolvedMethod._resolvedZone.name}` : resolvedMethod._zoneFallback ? ' · Other' : '';
      return {
        ...prev,
        shippingMethodId: resolvedMethod._id || resolvedMethod.id,
        shippingMethod: resolvedMethod.name + zoneSuffix + (useExpress && resolvedMethod.expressOption?.label ? ` (${resolvedMethod.expressOption.label})` : ''),
        shippingMethodSnapshot: { name: resolvedMethod.name, type: resolvedMethod.type, price, providerName: resolvedMethod.providerName },
        shipping: price,
        totalPrice,
      };
    });
    setSelectedShippingMethod(resolvedMethod);
    setExpressSelected(useExpress);
  };

  const handleSelectShippingMethod = (method: any) => {
    applyShippingMethod(method, false);
    setShowShippingMethodModal(false);
  };

  const handleToggleExpress = () => {
    if (!selectedShippingMethod) return;
    applyShippingMethod(selectedShippingMethod, !expressSelected);
  };

  const handleClearShippingMethod = () => {
    setFormData(prev => {
      const subtotal = prev.subtotal || 0;
      const tax = prev.tax || 0;
      const discount = prev.discount || 0;
      return {
        ...prev,
        shippingMethodId: undefined,
        shippingMethod: '',
        shippingMethodSnapshot: undefined,
        shipping: 0,
        totalPrice: subtotal + tax - discount,
      };
    });
    setSelectedShippingMethod(null);
    setExpressSelected(false);
  };

  const loadOrder = async () => {
    if (!orderId) {
      showNotification('error', t('orders.common.error'), t('orders.form.orderIdRequired'));
      handleCancel();
      return;
    }

    try {
      setLoadingOrder(true);
      const response = await apiService.getOrderById(orderId);
      
      if (response.statusCode === 1000 && response.data) {
        const order = response.data;
        setOrderNumber(order.orderNumber || order._id || '');
        setQuotationId(order.quotationId || undefined);
        setFormData({
          customer: {
            name: order.customer.name,
            phone: order.customer.phone,
            email: order.customer.email || '',
            address: order.customer.address || '',
            location: order.customer.location,
          },
          items: await Promise.all((order.items || []).map(async (item: any) => {
            // Normalize a raw API addon so it always has both id and _id
            const normalizeAddon = (a: any) => ({ ...a, id: a.id || a._id, _id: a._id || a.id });
            // Fetch full product to get the addons list for the addon picker
            if (item.productId || item.product?._id) {
              try {
                const productRes = await apiService.getProductById(item.productId || item.product._id);
                if (productRes.statusCode === 1000 && productRes.data) {
                  return {
                    ...item,
                    product: {
                      ...item.product,
                      addons: (productRes.data.addons || []).map(normalizeAddon),
                      inventoryTracking: productRes.data.inventoryTracking,
                      inventoryQuantity: productRes.data.inventoryQuantity,
                      vendorIds: productRes.data.vendorIds || [],
                    },
                    selectedAddons: (item.selectedAddons || []).map((a: any) => ({
                      addonId: a.addonId || a._id || a.id,
                      name: a.name,
                      price: a.price,
                      currency: a.currency,
                      quantity: a.quantity ?? 1,
                    })),
                  };
                }
              } catch { /* fallback below */ }
            }
            return {
              ...item,
              product: { ...item.product, addons: (item.product?.addons || []).map(normalizeAddon) },
              selectedAddons: (item.selectedAddons || []).map((a: any) => ({
                addonId: a.addonId || a._id || a.id,
                name: a.name,
                price: a.price,
                currency: a.currency,
                quantity: a.quantity ?? 1,
              })),
            };
          })),
          orderStatus: order.orderStatus,
          paymentStatus: order.paymentStatus || 'unpaid',
          shippingAddress: order.shippingAddress || '',
          shippingLocation: order.shippingLocation || undefined,
          shippingMethod: order.shippingMethod || '',
          shippingMethodId: order.shippingMethodId,
          shippingMethodSnapshot: order.shippingMethodSnapshot,
          trackingNumber: order.trackingNumber || '',
          notes: (() => {
            // Handle notes - can be array, JSON string, or plain string
            if (!order.notes) return [];
            if (Array.isArray(order.notes)) {
              // Ensure all notes have proper structure
              return order.notes.map((note: any) => {
                // If note is already a proper Note object, return it
                if (typeof note === 'object' && note.content) {
                  return {
                    ...note,
                    createdAt: note.createdAt ? (typeof note.createdAt === 'string' ? new Date(note.createdAt) : note.createdAt) : new Date(),
                  };
                }
                // If note is a string, convert to Note object
                if (typeof note === 'string') {
                  return {
                    content: note,
                    createdBy: { id: '', email: '' },
                    createdAt: new Date(),
                  };
                }
                return note;
              });
            }
            // If notes is a JSON string, parse it
            if (typeof order.notes === 'string') {
              try {
                const parsed = JSON.parse(order.notes);
                if (Array.isArray(parsed)) {
                  return parsed.map((note: any) => ({
                    ...note,
                    createdAt: note.createdAt ? (typeof note.createdAt === 'string' ? new Date(note.createdAt) : new Date(note.createdAt)) : new Date(),
                  }));
                }
                // If parsed is a single object, wrap in array
                if (parsed && typeof parsed === 'object' && parsed.content) {
                  return [{
                    ...parsed,
                    createdAt: parsed.createdAt ? (typeof parsed.createdAt === 'string' ? new Date(parsed.createdAt) : new Date(parsed.createdAt)) : new Date(),
                  }];
                }
              } catch (e) {
                // If parsing fails, treat as plain string
                console.warn('[AddOrderScreen] Failed to parse notes JSON, treating as plain string:', e);
              }
              // Treat as plain string note
              return [{
                content: order.notes,
                createdBy: { id: '', email: '' },
                createdAt: new Date(),
              }];
            }
            return [];
          })(),
          subtotal: order.subtotal || 0,
          tax: order.tax || 0,
          extraCharges: order.extraCharges || [],
          shipping: order.shipping || 0,
          discount: order.discount || 0,
          manualDiscount: order.manualDiscount || 0,
          totalPrice: order.totalPrice,
          currency: order.currency || businessCurrency(),
        });

        // Restore coupon state if the order had one applied
        if (order.couponCode) {
          const couponDiscount = (order.discount || 0) - (order.manualDiscount || 0);
          setAppliedCoupon({ code: order.couponCode, discountAmount: Math.max(0, couponDiscount) });
          setCouponCode(order.couponCode);
        }

        // Store original item quantities for stock-check in edit mode
        const qtyMap = new Map<string, number>();
        for (const item of order.items || []) {
          const key = `${item.productId || item.product?._id}::${item.variantId || ''}`;
          qtyMap.set(key, (qtyMap.get(key) || 0) + item.quantity);
        }
        originalItemQtyRef.current = qtyMap;

        // Restore selected shipping method state from snapshot
        if (order.shippingMethodId && order.shippingMethodSnapshot) {
          setSelectedShippingMethod({
            _id: order.shippingMethodId,
            ...order.shippingMethodSnapshot,
          });
        }

        // Load and set parent contact if order has contactId
        if (order.contactId) {
          try {
            // Load parent contact by ID
            const contactResponse = await apiService.getParentContactById(order.contactId);
            
            if (contactResponse.statusCode === 1000 && contactResponse.data) {
              const parentContact = contactResponse.data;
              // Map parent contact to Contact format
              // Spread parentContact first, then override with specific fields to ensure profilePictureUrl is preserved
              const contact = {
                ...parentContact,
                id: parentContact._id || parentContact.id,
                _id: parentContact._id || parentContact.id,
                name: parentContact.profile?.name || order.customer.name,
                phoneNumber: parentContact.profile?.phone || order.customer.phone,
                phone: parentContact.profile?.phone || order.customer.phone,
                email: parentContact.profile?.email || order.customer.email,
                address: parentContact.profile?.location?.address || order.customer.address,
                location: parentContact.profile?.location || order.customer.location,
                profilePictureUrl: parentContact.profile?.avatar, // Set after spread to ensure it's preserved
              };
              setSelectedContact(contact);
              setOriginalContactId(parentContact._id || parentContact.id); // Store original contact ID
              console.log('[AddOrderScreen] Parent contact loaded for order:', contact);
            } else {
              // If parent contact not found, create a contact object from order customer data
              // This ensures the ContactSelector shows the customer info
              const contactFromOrder = {
                id: order.contactId,
                _id: order.contactId,
                name: order.customer.name,
                phoneNumber: order.customer.phone,
                phone: order.customer.phone,
                email: order.customer.email,
                address: order.customer.address,
                location: order.customer.location,
              };
              setSelectedContact(contactFromOrder);
              setOriginalContactId(order.contactId); // Store original contact ID
              console.log('[AddOrderScreen] Created contact object from order data (parent contact not found):', contactFromOrder);
            }
          } catch (contactError: any) {
            console.warn('[AddOrderScreen] Could not load parent contact:', contactError);
            // If contact loading fails, create a contact object from order customer data
            const contactFromOrder = {
              id: order.contactId,
              _id: order.contactId,
              name: order.customer.name,
              phoneNumber: order.customer.phone,
              phone: order.customer.phone,
              email: order.customer.email,
              address: order.customer.address,
              location: order.customer.location,
            };
            setSelectedContact(contactFromOrder);
            setOriginalContactId(order.contactId); // Store original contact ID
          }
        }

        // Re-fetch taxes & charges from business config so totals are current
        const loadedItems = (order.items || []) as OrderItem[];
        if (loadedItems.length > 0) {
          recalculateTotals(loadedItems);
        }

      } else {
        showNotification('error', t('orders.common.error'), t('orders.form.loadFailed'));
        setTimeout(() => handleCancel(), 2000);
      }
    } catch (error: any) {
      console.error('Error loading order:', error);
      showNotification('error', t('orders.common.error'), error.message || t('orders.form.loadFailed'));
      setTimeout(() => handleCancel(), 2000);
    } finally {
      setLoadingOrder(false);
    }
  };

  const handleContactSelect = async (contact: any) => {
    // Handle null contact (removal)
    if (!contact) {
      setSelectedContact(null);
      // In edit mode, don't clear originalContactId - we'll handle contact removal separately
      if (mode === 'new') {
        setOriginalContactId(null);
      }
      setFormData(prev => ({
        ...prev,
        customer: {
          name: '',
          phone: '',
          email: '',
          address: '',
          location: undefined,
        }
      }));
      return;
    }

    setSelectedContact(contact);
    // Track contact ID for change detection in edit mode
    if (mode === 'edit' && !originalContactId) {
      // Only set if not already set (preserve original)
      const contactId = contact._id || contact.id;
      if (contactId) {
        setOriginalContactId(contactId);
      }
    }
    setFormData(prev => ({
      ...prev,
      customer: {
        name: contact.name || '',
        phone: contact.phoneNumber || contact.phone || '',
        email: Array.isArray(contact.email) ? (contact.email[0] || '') : (contact.email || ''),
        address: contact.address || '',
        location: contact.location,
      }
    }));
    
    // If contact doesn't have _id (new contact), create parent contact
    if (!contact._id && !contact.id && contact.phone) {
      try {
        // First, search for existing parent contact by phone
        const searchResponse = await apiService.searchParentContactsByIdentifiers({
          phone: contact.phoneNumber || contact.phone,
        });
        
        let parentContactId: string | undefined;
        
        if (searchResponse.statusCode === 1000 && searchResponse.data && Array.isArray(searchResponse.data) && searchResponse.data.length > 0) {
          // Found existing parent contact
          parentContactId = searchResponse.data[0]._id || searchResponse.data[0].id;
        } else {
          // Create new parent contact
          const createResponse = await apiService.createParentContact({
            primaryIdentifier: contact.phoneNumber || contact.phone,
            primaryIdentifierType: 'phone',
            identifiers: {
              phone: [contact.phoneNumber || contact.phone], // Backend expects arrays
              ...(contact.email ? { email: [contact.email] } : {}), // Backend expects arrays
            },
            profile: {
              name: contact.name || '',
              phone: contact.phoneNumber || contact.phone,
              email: contact.email,
              location: contact.location,
            },
          });
          
          if (createResponse.statusCode === 1000 && createResponse.data) {
            parentContactId = createResponse.data._id || createResponse.data.id;
            // Update selected contact with new ID
            setSelectedContact({ ...contact, _id: parentContactId, id: parentContactId });
          }
        }
      } catch (error) {
        console.error('Error creating/linking parent contact:', error);
        // Continue with order creation even if parent contact creation fails
      }
    }

    // Capture the new contact's location now, before any async state updates make it stale
    const contactLocation = contact.location?.latitude !== undefined
      ? { latitude: contact.location.latitude, longitude: contact.location.longitude }
      : undefined;

    // Re-resolve product prices if contact has a location
    if (contact.location?.latitude || contact.location?.longitude) {
      setFormData(prev => {
        if (prev.items.length === 0) return prev;
        repriceItemsForLocation(prev.items as OrderItem[], contact.location).then(repriced => {
          setFormData(p => ({ ...p, items: repriced }));
          recalculateTotals(repriced as OrderItem[], contactLocation);
        });
        return prev;
      });
    }

    // Re-fetch charges when contact changes (contact-targeted charges may apply)
    if (currentBusiness?._id) {
      const newContactId = contact._id || contact.id;
      setFormData(prev => {
        if (prev.items.length === 0) return prev;
        const items = prev.items as OrderItem[];
        const subtotal = items.reduce((s, i) => s + i.totalPrice, 0);
        const productTypes = [...new Set(items.map(i => i.product?.productType).filter(Boolean))] as ('physical' | 'digital' | 'service')[];
        const orderProductIds = items.map((i: any) => i.productId).filter(Boolean);
        apiService.previewCharges({
          subtotal,
          productTypes: productTypes.length ? productTypes : ['physical'],
          orderProductIds,
          contactId: newContactId,
          location: contactLocation,   // use the freshly-captured location, not stale selectedContact
        }).then(res => {
          if (res.statusCode === 1000 && res.data) {
            const { taxes, taxAmount, charges } = res.data;
            const extraCharges = [
              ...taxes.map((t: any) => ({ name: `${t.name} (${t.rate}%)`, amount: t.amount })),
              ...charges,
            ];
            setFormData(p => {
              const chargesTotal = extraCharges.reduce((s, c) => s + c.amount, 0);
              const totalPrice = Math.max(0, subtotal + taxAmount + (p.shipping || 0) + chargesTotal - (p.discount || 0));
              return { ...p, tax: taxAmount, extraCharges, totalPrice };
            });
          }
        }).catch(() => {});
        return prev;
      });
    }
  };

  const handleContactUpdate = async (contactId: string, updates: any) => {
    // This would call the API to update contact
    // For now, just update local state
    if (selectedContact && (selectedContact.id === contactId || selectedContact._id === contactId)) {
      setSelectedContact({ ...selectedContact, ...updates });
      setFormData(prev => ({
        ...prev,
        customer: {
          ...prev.customer,
          ...updates,
        }
      }));
    }
  };

  // Check if order can be edited (not shipped, delivered, or cancelled)
  const canEditItems = () => {
    // In edit mode, allow editing items unless order is cancelled
    // Notes are always editable (handled separately)
    if (mode === 'edit') {
      const status = formData.orderStatus?.toLowerCase();
      return status !== 'cancelled';
    }
    // In new mode, always allow editing
    return true;
  };

  /**
   * Re-resolve location-based prices for all cart items when the customer location changes.
   * Returns the repriced items array (so the caller can pass it to recalculateTotals).
   */
  const repriceItemsForLocation = async (
    items: OrderItem[],
    location?: { latitude?: number; longitude?: number },
  ): Promise<OrderItem[]> => {
    if (!location?.latitude && !location?.longitude) return items;
    const repriced = await Promise.all(
      items.map(async (item) => {
        try {
          const res = await apiService.resolveProductPrice(item.productId, {
            latitude: location.latitude,
            longitude: location.longitude,
          });
          if (res.statusCode === 1000 && (res.data?.matched || res.data?.addons?.length)) {
            const bizCurrency = orderCurrencyRef.current || currentBusiness?.currency || 'USD';
            // product's own currency is the source currency for conversion
            const productBaseCurrency = res.data!.currency || item.product?.currency || bizCurrency;

            // Convert product unit price to business currency
            let newUnitPrice = item.unitPrice;
            if (res.data!.matched) {
              const rawPrice = res.data!.salePrice ?? res.data!.price;
              newUnitPrice = await convertAmount(rawPrice, productBaseCurrency, bizCurrency);
            }

            // Build addon map with prices converted to business currency
            // Addon zone currency overrides; fall back to the product's base currency (not bizCurrency)
            const resolvedAddonMap: Record<string, { price: number; currency?: string }> = {};
            for (const ra of res.data!.addons ?? []) {
              const addonCurrency = ra.currency || productBaseCurrency;
              const convertedPrice = await convertAmount(ra.price, addonCurrency, bizCurrency);
              resolvedAddonMap[ra.id] = { price: convertedPrice, currency: bizCurrency };
            }

            const newSelectedAddons = (item.selectedAddons || []).map(sa =>
              resolvedAddonMap[sa.addonId] !== undefined
                ? { ...sa, price: resolvedAddonMap[sa.addonId].price, currency: bizCurrency }
                : sa
            );

            // Update product.addons with converted prices so the add-addon picker shows correct values
            const newProductAddons = (item.product?.addons || []).map((a: any) =>
              resolvedAddonMap[a.id] !== undefined
                ? { ...a, price: resolvedAddonMap[a.id].price, currency: bizCurrency }
                : a
            );

            return {
              ...item,
              unitPrice: newUnitPrice,
              selectedAddons: newSelectedAddons,
              totalPrice: calcItemTotal(newUnitPrice, item.quantity, newSelectedAddons),
              product: {
                ...item.product,
                ...(res.data!.matched ? {
                  price: newUnitPrice,
                  salePrice: undefined, // already folded into newUnitPrice
                  currency: bizCurrency,
                } : {}),
                addons: newProductAddons,
              },
            };
          }
        } catch (_) {/* non-fatal */}
        return item;
      })
    );
    return repriced;
  };

  // Recalculate totals based on items, fetching tax/charges from business config.
  // Pass `locationOverride` when the location has just changed and selectedContact/formData
  // are still stale (e.g. immediately after contact selection or map pick).
  const recalculateTotals = async (
    items: OrderItem[],
    locationOverride?: { latitude?: number; longitude?: number },
  ) => {
    const subtotal = items.reduce((sum, item) => sum + item.totalPrice, 0);

    // Optimistic update with existing charges while we fetch
    setFormData(prev => {
      const tax = prev.tax || 0;
      // Exclude tax lines from chargesTotal — tax is already in `tax`
      const chargesTotal = (prev.extraCharges ?? [])
        .filter((c: any) => !c._isTax)
        .reduce((s, c) => s + c.amount, 0);
      const discount = prev.discount || 0;
      let shipping = prev.shipping || 0;
      let snapshot = prev.shippingMethodSnapshot;
      if (selectedShippingMethod) {
        shipping = calcShippingPrice(selectedShippingMethod, subtotal, expressSelected);
        if (snapshot) snapshot = { ...snapshot, price: shipping };
      }
      const totalPrice = Math.max(0, subtotal + tax + shipping + chargesTotal - discount);
      return { ...prev, subtotal, shipping, shippingMethodSnapshot: snapshot, totalPrice };
    });

    if (!currentBusiness?._id || items.length === 0) return;

    // Fetch fresh tax + charges from business config
    setLoadingCharges(true);
    try {
      const productTypes = [...new Set(items.map(i => i.product?.productType).filter(Boolean))] as ('physical' | 'digital' | 'service')[];
      const orderProductIds = items.map(i => i.productId).filter(Boolean);
      const res = await apiService.previewCharges({
        subtotal,
        productTypes: productTypes.length ? productTypes : ['physical'],
        orderProductIds,
        contactId: selectedContact?._id,
        location: locationOverride ?? getDeliveryLocation(),
      });
      if (res.statusCode === 1000 && res.data) {
        const { taxes, taxAmount, charges } = res.data;

        // Separate mandatory (auto-applied) from optional (user-toggled)
        const mandatory = charges.filter((c: any) => c.mandatory !== false);
        const optional = charges.filter((c: any) => c.mandatory === false);

        // Preserve checked state for optional charges already selected
        setOptionalCharges(prev => optional.map((c: any) => {
          const existing = prev.find(p => p.name === c.name);
          return { name: c.name, amount: c.amount, checked: existing?.checked ?? false };
        }));

        // extraCharges in formData = taxes + mandatory charges only
        // optional charges are added via their checked state
        const mandatoryCharges = [
          ...taxes.map((t: any) => ({ name: `${t.name} (${t.rate}%)`, amount: t.amount, _isTax: true })),
          ...mandatory.map((c: any) => ({ name: c.name, amount: c.amount })),
        ];
        setFormData(prev => {
          // Carry over any previously checked optional charges
          const checkedOptional = optional
            .filter((c: any) => prev.extraCharges?.some((e: any) => e.name === c.name && e._optional))
            .map((c: any) => ({ name: c.name, amount: c.amount, _optional: true }));
          const extraCharges = [...mandatoryCharges, ...checkedOptional];
          // chargesTotal must NOT include tax lines — taxAmount is added separately
          const mandatoryChargesTotal = mandatory.reduce((s: number, c: any) => s + c.amount, 0);
          const checkedOptionalTotal = checkedOptional.reduce((s, c) => s + c.amount, 0);
          const chargesTotal = mandatoryChargesTotal + checkedOptionalTotal;
          const discount = prev.discount || 0;
          const totalPrice = Math.max(0, subtotal + taxAmount + (prev.shipping || 0) + chargesTotal - discount);
          return { ...prev, subtotal, tax: taxAmount, extraCharges, totalPrice };
        });
      }
    } catch (_) {
      // Non-blocking — totals already updated optimistically above
    } finally {
      setLoadingCharges(false);
    }
  };

  // Calculate item total including addons
  const calcItemTotal = (unitPrice: number, quantity: number, selectedAddons: SelectedAddon[] = []): number => {
    const addonsTotal = selectedAddons.reduce((sum, a) => sum + a.price * a.quantity, 0);
    return (unitPrice * quantity) + addonsTotal;
  };

  // Update item quantity
  // ── Line vendors: each line is supplied by one vendor (the product's primary vendor unless changed) ──
  const [vendorNames, setVendorNames] = useState<Record<string, string>>({});
  const [showPackingSlips, setShowPackingSlips] = useState(false);
  const [showDocuments, setShowDocuments] = useState(false);
  const lineVendorIds = useMemo(
    () => [...new Set(formData.items.flatMap((i) => [...(i.product.vendorIds || []), ...(i.vendorId ? [i.vendorId] : [])]))],
    [formData.items],
  );
  useEffect(() => {
    const missing = lineVendorIds.filter((id) => !vendorNames[id]);
    if (!missing.length) return;
    apiService.getVendors({ limit: 500 }).then((res) => {
      const names: Record<string, string> = {};
      for (const v of res.data?.vendors || []) names[v._id] = v.name;
      for (const i of formData.items) if (i.vendorId && i.vendorName && !names[i.vendorId]) names[i.vendorId] = i.vendorName;
      for (const id of missing) if (!names[id]) names[id] = '—';
      setVendorNames((prev) => ({ ...prev, ...names }));
    }).catch(() => {});
  }, [lineVendorIds.join(',')]);
  const setItemVendor = (index: number, vendorId: string) => setFormData((prev) => ({
    ...prev,
    items: prev.items.map((it, i) => (i === index ? { ...it, vendorId, vendorName: vendorNames[vendorId] } : it)),
  }));

  const updateItemQuantity = (index: number, newQuantity: number) => {
    if (newQuantity < 1) {
      removeItem(index);
      return;
    }

    const updatedItems = [...formData.items];
    const item = updatedItems[index];
    updatedItems[index] = {
      ...item,
      quantity: newQuantity,
      totalPrice: calcItemTotal(item.unitPrice, newQuantity, item.selectedAddons),
    };

    setFormData(prev => ({
      ...prev,
      items: updatedItems,
    }));

    recalculateTotals(updatedItems);
  };

  // Toggle addon selection for an order item
  const toggleItemAddon = (itemIndex: number, addon: ProductAddon) => {
    const updatedItems = [...formData.items];
    const item = updatedItems[itemIndex];
    const currentAddons = item.selectedAddons || [];
    const existingIdx = currentAddons.findIndex(a => a.addonId === addon.id);

    let newAddons: SelectedAddon[];
    if (existingIdx >= 0) {
      // Remove addon
      newAddons = currentAddons.filter(a => a.addonId !== addon.id);
    } else {
      // Add addon with quantity 1
      newAddons = [...currentAddons, { addonId: addon.id, name: addon.name, price: addon.price, currency: addon.currency, quantity: 1 }];
    }

    updatedItems[itemIndex] = {
      ...item,
      selectedAddons: newAddons,
      totalPrice: calcItemTotal(item.unitPrice, item.quantity, newAddons),
    };

    setFormData(prev => ({ ...prev, items: updatedItems }));
    recalculateTotals(updatedItems);
  };

  // Update quantity of a selected addon for an order item
  const updateAddonQuantity = (itemIndex: number, addonId: string, delta: number, maxQuantity?: number) => {
    const updatedItems = [...formData.items];
    const item = updatedItems[itemIndex];
    const currentAddons = item.selectedAddons || [];
    const addonIdx = currentAddons.findIndex(a => a.addonId === addonId);
    if (addonIdx < 0) return;

    const newQty = currentAddons[addonIdx].quantity + delta;
    let newAddons: SelectedAddon[];
    if (newQty < 1) {
      // Remove if decremented below 1
      newAddons = currentAddons.filter(a => a.addonId !== addonId);
    } else if (maxQuantity && newQty > maxQuantity) {
      return; // Cap at maxQuantity
    } else {
      newAddons = currentAddons.map((a, i) => i === addonIdx ? { ...a, quantity: newQty } : a);
    }

    updatedItems[itemIndex] = {
      ...item,
      selectedAddons: newAddons,
      totalPrice: calcItemTotal(item.unitPrice, item.quantity, newAddons),
    };

    setFormData(prev => ({ ...prev, items: updatedItems }));
    recalculateTotals(updatedItems);
  };

  // Remove item from order
  const removeItem = (index: number) => {
    setFormData(prev => {
      const updatedItems = prev.items.filter((_, i) => i !== index);
      recalculateTotals(updatedItems);
      return { ...prev, items: updatedItems };
    });
  };

  // Handle add product - show product selector modal
  const handleAddProduct = () => {
    setShowProductSelector(true);
  };

  // Handle product selection confirmation
  const handleProductsSelected = async (selectedProductIds: string[], aiLines?: Array<{ productId: string; variantId?: string; quantity: number; requestedUnit?: string }>) => {
    if (selectedProductIds.length === 0) {
      setShowProductSelector(false);
      return;
    }

    try {
      setAddingProducts(true);

      // Start with existing items
      const updatedItems = [...formData.items];
      let addedCount = 0;
      let consolidatedCount = 0;

      for (const [selectionIndex, productId] of selectedProductIds.entries()) {
        try {
          // Get product details
          const productResponse = await apiService.getProductById(productId);
          
          if (productResponse.statusCode === 1000 && productResponse.data) {
            const product = productResponse.data;
            const finalProductId = product._id || productId;
            const requested = aiLines?.[selectionIndex];
            if (requested && (requested.productId !== productId || !Number.isInteger(requested.quantity) || requested.quantity <= 0 || requested.quantity > 10000)) throw new Error('Invalid AI line quantity.');
            const quantity = requested?.quantity ?? 1;
            const selectedVariant = requested?.variantId ? product.variants?.find((v: any) => v.id === requested.variantId) : undefined;
            if (requested?.variantId && !selectedVariant) throw new Error('The requested product size is no longer available.');

            // Resolve location-based price and addon prices, then convert to business currency
            const bizCurrency = orderCurrencyRef.current || currentBusiness?.currency || 'USD';
            const basePrice = selectedVariant?.price ?? product.salePrice ?? product.price ?? 0;
            const baseCurrency = product.currency || bizCurrency;
            let resolvedAddonMap: Record<string, { price: number; currency: string }> = {};

            // Start with base product price converted to business currency
            let unitPrice = await convertAmount(basePrice, baseCurrency, bizCurrency);

            const hasZones = product.pricingZones?.length || product.addons?.some((a: any) => a.pricingZones?.length);
            if (hasZones) {
              const loc = formData.shippingLocation || formData.customer?.location;
              try {
                const priceRes = await apiService.resolveProductPrice(finalProductId, {
                  latitude: loc?.latitude,
                  longitude: loc?.longitude,
                });
                if (priceRes.statusCode === 1000 && priceRes.data) {
                  if (priceRes.data.matched) {
                    const rawPrice = priceRes.data.salePrice ?? priceRes.data.price;
                    const zoneCurrency = priceRes.data.currency || baseCurrency;
                    unitPrice = await convertAmount(rawPrice, zoneCurrency, bizCurrency);
                  }
                  for (const ra of priceRes.data.addons ?? []) {
                    // Addon zone currency → product base currency → bizCurrency (never fall back to bizCurrency as source)
                    const addonCurrency = ra.currency || baseCurrency;
                    const convertedPrice = await convertAmount(ra.price, addonCurrency, bizCurrency);
                    resolvedAddonMap[ra.id] = { price: convertedPrice, currency: bizCurrency };
                  }
                }
              } catch (_) {/* non-fatal — fall through to base price */}
            }

            // Build addon list with zone-resolved + converted prices
            const resolvedAddons = await Promise.all(
              (product.addons || []).map(async (a: any) =>
                resolvedAddonMap[a.id] !== undefined
                  ? { ...a, price: resolvedAddonMap[a.id].price, currency: bizCurrency }
                  : { ...a, price: await convertAmount(a.price, a.currency || baseCurrency, bizCurrency), currency: bizCurrency }
              )
            );
            
            // Check if product already exists in order (by productId, and optionally variantId)
            const existingItemIndex = updatedItems.findIndex(
              item => item.productId === finalProductId && item.variantId === selectedVariant?.id
            );

            if (existingItemIndex >= 0) {
              // Product already exists - consolidate by increasing quantity
              // Also apply the zone-resolved price in case location changed since last add
              const existingItem = updatedItems[existingItemIndex];
              const newQuantity = existingItem.quantity + quantity;
              // Re-apply zone-resolved addon prices to any already-selected addons
              const updatedSelectedAddons = (existingItem.selectedAddons || []).map(sa =>
                resolvedAddonMap[sa.addonId] !== undefined
                  ? { ...sa, price: resolvedAddonMap[sa.addonId].price, currency: resolvedAddonMap[sa.addonId].currency ?? sa.currency }
                  : sa
              );
              updatedItems[existingItemIndex] = {
                ...existingItem,
                unitPrice,
                quantity: newQuantity,
                selectedAddons: updatedSelectedAddons,
                totalPrice: calcItemTotal(unitPrice, newQuantity, updatedSelectedAddons),
                product: { ...existingItem.product, price: unitPrice, salePrice: undefined, currency: bizCurrency, addons: resolvedAddons },
              };
              consolidatedCount++;
            } else {
              // New product - add as new item
              const orderItem: OrderItem = {
                productId: finalProductId,
                variantId: selectedVariant?.id,
                quantity,
                product: {
                  _id: finalProductId,
                  name: product.name,
                  price: unitPrice,
                  salePrice: undefined,
                  currency: bizCurrency,
                  primaryImage: product.primaryImage,
                  brand: product.brand,
                  sku: product.sku,
                  productType: product.productType,
                  addons: resolvedAddons,
                  priceUnit: product.priceUnit || requested?.requestedUnit,
                  unitId: product.unitId,
                  inventoryTracking: product.inventoryTracking,
                  inventoryQuantity: product.inventoryQuantity,
                  vendorIds: product.vendorIds || [],
                },
                vendorId: product.vendorIds?.[0],
                variant: selectedVariant ? { id: selectedVariant.id, name: selectedVariant.name, price: unitPrice } : undefined,
                selectedAddons: [],
                unitPrice: unitPrice,
                totalPrice: unitPrice * quantity,
              };
              updatedItems.push(orderItem);
              addedCount++;
            }
          }
        } catch (error) {
          console.error(`Error loading product ${productId}:`, error);
        }
      }

      if (addedCount > 0 || consolidatedCount > 0) {
        // Item prices are already converted into the order currency, so the
        // order keeps its own currency (no longer overridden by the first
        // product's currency).
        setFormData(prev => ({
          ...prev,
          items: updatedItems,
        }));
        
        // Recalculate totals
        recalculateTotals(updatedItems);
        
        // Show appropriate notification
        let message = '';
        if (addedCount > 0 && consolidatedCount > 0) {
          message = `Added ${addedCount} ${addedCount === 1 ? 'product' : 'products'} and updated quantity for ${consolidatedCount} ${consolidatedCount === 1 ? 'product' : 'products'}`;
        } else if (addedCount > 0) {
          message = `Added ${addedCount} ${addedCount === 1 ? 'product' : 'products'} to order`;
        } else if (consolidatedCount > 0) {
          message = `Updated quantity for ${consolidatedCount} ${consolidatedCount === 1 ? 'product' : 'products'}`;
        }
        showNotification('success', t('orders.common.success'), message);
      } else {
        showNotification('error', t('orders.common.error'), t('orders.form.productLoadFailed'));
      }
    } catch (error: any) {
      console.error('Error adding products to order:', error);
      showNotification('error', t('orders.common.error'), error.message || t('orders.form.addProductsFailed'));
    } finally {
      setAddingProducts(false);
      setShowProductSelector(false);
    }
  };


  const aiProductsAppliedRef = useRef(false);
  useEffect(() => {
    const ids = routeParams?.prefillOrder?.aiProductIds;
    if (mode !== 'new' || aiProductsAppliedRef.current || !Array.isArray(ids)) return;
    aiProductsAppliedRef.current = true;
    const lines = routeParams?.prefillOrder?.aiLines;
    const validIds = ids.filter((id: unknown) => typeof id === 'string' && /^[a-f\d]{24}$/i.test(id)).slice(0, 10);
    void handleProductsSelected(validIds, Array.isArray(lines) ? lines.slice(0, 10) : undefined);
  }, [mode, routeParams?.prefillOrder?.aiProductIds]);

  // Auto-add scanned product to new order when navigating from scanner
  useEffect(() => {
    if (mode !== 'new' || scannedProductInjected) {
      return;
    }

    const scannedProductId = routeParams?.scannedProductId;
    if (scannedProductId) {
      setScannedProductInjected(true);
      handleProductsSelected([scannedProductId]);
      showNotification('success', t('orders.form.productAdded'), t('orders.form.scannedAdded'));
    }
  }, [mode, routeParams?.scannedProductId, scannedProductInjected, handleProductsSelected, showNotification]);

  useEffect(() => {
    const prefill = routeParams?.prefillOrder;
    const location = prefill?.location;
    const customer = prefill?.customer || {};
    if (
      mode !== 'new' ||
      orderLocationPrefillAppliedRef.current ||
      !prefill
    ) {
      return;
    }

    orderLocationPrefillAppliedRef.current = true;

    const normalizedLocation = location &&
      typeof location.latitude === 'number' &&
      typeof location.longitude === 'number'
        ? {
            latitude: location.latitude,
            longitude: location.longitude,
            address: location.address || location.text || '',
            text: location.text || location.address || '',
            ...(location.city && { city: location.city }),
            ...(location.country && { country: location.country }),
          }
        : customer.location &&
          typeof customer.location.latitude === 'number' &&
          typeof customer.location.longitude === 'number'
          ? {
              latitude: customer.location.latitude,
              longitude: customer.location.longitude,
              address: customer.location.address || customer.location.text || '',
              text: customer.location.text || customer.location.address || '',
              ...(customer.location.city && { city: customer.location.city }),
              ...(customer.location.country && { country: customer.location.country }),
            }
          : undefined;
    const contactId = prefill.contactId || customer.id;

    if (contactId || customer.name || customer.phone) {
      setSelectedContact({
        ...customer,
        _id: contactId,
        id: contactId,
        name: customer.name || '',
        phoneNumber: customer.phoneNumber || customer.phone || '',
        phone: customer.phone || customer.phoneNumber || '',
        location: normalizedLocation,
      });
      if (contactId) {
        setOriginalContactId(contactId);
      }
    }

    setFormData(prev => ({
      ...prev,
      customer: {
        ...prev.customer,
        name: customer.name || prev.customer.name,
        phone: customer.phoneNumber || customer.phone || prev.customer.phone,
        email: customer.email || prev.customer.email,
        address: normalizedLocation?.address || customer.address || prev.customer.address,
        location: normalizedLocation,
      },
      shippingAddress: normalizedLocation?.address || prev.shippingAddress,
      shippingLocation: normalizedLocation || prev.shippingLocation,
    }));

    showNotification('success', t('orders.form.orderCustomer'), t('orders.form.customerPrefilled'));
  }, [mode, routeParams?.prefillOrder, showNotification]);


  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!selectedContact && !formData.customer.name.trim()) {
      newErrors.customerName = t('orders.form.errCustomerName');
    }

    if (!selectedContact && !formData.customer.phone.trim()) {
      newErrors.customerPhone = t('orders.form.errCustomerPhone');
    }

    if (formData.items.length === 0) {
      newErrors.items = t('orders.form.errItems');
    }

    if (formData.totalPrice <= 0) {
      newErrors.totalPrice = t('orders.form.errTotal');
    }

    // Block if free shipping threshold not met and no fallback cost configured
    if (
      selectedShippingMethod &&
      isFreeThresholdNotMet(selectedShippingMethod, formData.subtotal) &&
      (selectedShippingMethod.belowMinCost == null || selectedShippingMethod.belowMinCost === 0)
    ) {
      newErrors.shippingMethod =
        `Order total (${formData.currency} ${formData.subtotal.toFixed(2)}) is below the free shipping minimum of ${formData.currency} ${selectedShippingMethod.minOrderAmount.toFixed(2)}. ` +
        `Please select a different shipping method or set a fallback cost on this method.`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      Alert.alert(t('orders.form.validationError'), t('orders.form.fixErrors'));
      return;
    }
    if (canEditItems() && !minimumsOk && minimums) {
      showNotification('error', t('orderMinimums.title'), minimums.violations.join('\n'));
      return;
    }

    // ── Stock check ─────────────────────────────────────────────────────────
    const overstockItems = formData.items.filter(item => {
      if (!item.product.inventoryTracking) return false;
      if (item.product.inventoryQuantity === undefined) return false;
      // In edit mode, the product's inventoryQuantity has already had the original
      // order quantities deducted. Add them back to get effective available stock.
      const key = `${item.productId}::${item.variantId || ''}`;
      const originalQty = mode === 'edit' ? (originalItemQtyRef.current.get(key) || 0) : 0;
      const effectiveAvailable = item.product.inventoryQuantity + originalQty;
      return item.quantity > effectiveAvailable;
    });

    if (overstockItems.length > 0) {
      const lines = overstockItems.map(item => {
        const key = `${item.productId}::${item.variantId || ''}`;
        const originalQty = mode === 'edit' ? (originalItemQtyRef.current.get(key) || 0) : 0;
        const effectiveAvailable = (item.product.inventoryQuantity ?? 0) + originalQty;
        return `• ${item.product.name}: ordered ${item.quantity}, available ${effectiveAvailable}`;
      });

      const message =
        `The following items exceed available stock:\n\n${lines.join('\n')}\n\n` +
        `Stock will go into negative. Do you want to proceed anyway?`;

      const approved = await new Promise<boolean>(resolve => {
        setStockWarning({ message, resolve });
      });

      if (!approved) return;
    }

    const allowNegativeStock = overstockItems.length > 0;
    // ────────────────────────────────────────────────────────────────────────

    try {
      setLoading(true);

      if (mode === 'new') {
        if (!currentBusiness?._id) {
          showNotification('error', t('orders.common.error'), t('orders.form.businessIdRequired'));
          return;
        }

        // Convert notes array to format expected by API
        // Ensure notes is properly formatted as array
        let notesForApi: string | Note[];
        if (Array.isArray(formData.notes)) {
          // Filter out empty notes and ensure proper format
          const validNotes = formData.notes.filter(note => note && note.content && note.content.trim());
          console.log('[AddOrderScreen] New order - Notes array:', formData.notes, 'Valid notes:', validNotes);
          if (validNotes.length > 0) {
            // Convert to JSON string for API
            notesForApi = JSON.stringify(validNotes);
            console.log('[AddOrderScreen] New order - Notes JSON:', notesForApi);
          } else {
            notesForApi = '';
            console.log('[AddOrderScreen] New order - No valid notes, using empty string');
          }
        } else if (formData.notes && typeof formData.notes === 'string') {
          notesForApi = formData.notes;
          console.log('[AddOrderScreen] New order - Notes as string:', notesForApi);
        } else {
          notesForApi = '';
          console.log('[AddOrderScreen] New order - Notes is empty or invalid, using empty string');
        }

        // Ensure we have parent contact ID
        let parentContactId: string | undefined = selectedContact?._id || selectedContact?.id;
        
        // If no parent contact ID but we have customer phone, try to find or create parent contact
        if (!parentContactId && formData.customer.phone) {
          try {
            const searchResponse = await apiService.searchParentContactsByIdentifiers({
              phone: formData.customer.phone,
            });
            
            if (searchResponse.statusCode === 1000 && searchResponse.data && Array.isArray(searchResponse.data) && searchResponse.data.length > 0) {
              parentContactId = searchResponse.data[0]._id || searchResponse.data[0].id;
            } else {
              // Create new parent contact
              const createResponse = await apiService.createParentContact({
                primaryIdentifier: formData.customer.phone,
                primaryIdentifierType: 'phone',
                identifiers: {
                  phone: [formData.customer.phone], // Backend expects arrays
                  ...(formData.customer.email ? { email: [formData.customer.email] } : {}), // Backend expects arrays
                },
                profile: {
                  name: formData.customer.name || '',
                  phone: formData.customer.phone,
                  email: formData.customer.email,
                  location: formData.customer.location,
                },
              });
              
              if (createResponse.statusCode === 1000 && createResponse.data) {
                parentContactId = createResponse.data._id || createResponse.data.id;
              }
            }
          } catch (error) {
            console.error('Error creating/linking parent contact:', error);
            // Continue with order creation even if parent contact creation fails
          }
        }

        const orderData = {
          customer: {
            ...formData.customer,
            email: Array.isArray(formData.customer.email)
              ? (formData.customer.email[0] || undefined)
              : (formData.customer.email || undefined),
          },
          items: formData.items.map(({ product: { addons: _addons, inventoryTracking: _it, inventoryQuantity: _iq, vendorIds: _vendorIds, ...product }, ...item }) => ({ ...item, product })),
          orderStatus: formData.orderStatus,
          paymentStatus: formData.paymentStatus,
          shippingAddress: formData.shippingAddress,
          shippingLocation: formData.shippingLocation,
          shippingMethod: formData.shippingMethod,
          shippingMethodId: formData.shippingMethodId,
          shippingMethodSnapshot: formData.shippingMethodSnapshot,
          notes: notesForApi,
          subtotal: formData.subtotal,
          tax: formData.tax,
          extraCharges: (formData.extraCharges ?? []).filter((c: any) => !c._optional || optionalCharges.some(o => o.name === c.name && o.checked)),
          shipping: formData.shipping,
          discount: formData.discount,
          manualDiscount: formData.manualDiscount,
          couponCode: appliedCoupon?.code,
          selectedChargeNames: optionalCharges.filter(c => c.checked).map(c => c.name),
          totalPrice: formData.totalPrice,
          currency: formData.currency,
          source: 'manual' as const,
          // Started from a visit in the contact profile — logged on that visit.
          ...(routeParams?.prefillOrder?.visitId ? { visitId: routeParams.prefillOrder.visitId } : {}),
          ...(parentContactId ? { contactId: parentContactId } : {}),
          ...(allowNegativeStock ? { allowNegativeStock: true } : {}),
        };

        const response = await apiService.createOrder(orderData);

        if (response.statusCode === 1000 && response.data) {
          showNotification('success', t('orders.common.success'), t('orders.form.created'));
          const createdOrderId = response.data._id || response.data.id;
          if (listId && createdOrderId) {
            try {
              await apiService.linkDataListRecords(listId, [createdOrderId]);
            } catch (err) {
              console.error('[AddOrderScreen] Failed to link new order to data list:', err);
            }
            setTimeout(() => navigateTo('data-list-records', listId), 1500);
          } else {
            setTimeout(() => navigateTo('orders'), 1500);
          }
        } else {
          const errorMessage =
            response.errors?.[0]?.message || response.message || 'Failed to create order';
          showNotification('error', t('orders.common.error'), errorMessage);
        }
      } else {
        // Update existing order
        if (!orderId) {
          showNotification('error', t('orders.common.error'), t('orders.form.orderIdRequired'));
          return;
        }

        // Handle contact changes and phone number updates
        let finalContactId: string | undefined = selectedContact?._id || selectedContact?.id;
        const currentContactId = finalContactId;
        const contactChanged = originalContactId && currentContactId !== originalContactId;
        const phoneChanged = canManageContacts && originalContactId && currentContactId === originalContactId &&
                            formData.customer.phone &&
                            selectedContact?.phoneNumber !== formData.customer.phone;

        // If contact changed, use new contact ID
        if (contactChanged) {
          console.log('[AddOrderScreen] Contact changed from', originalContactId, 'to', currentContactId);
          finalContactId = currentContactId;
        } 
        // If contact is same but phone number changed, add new phone to parent contact
        else if (phoneChanged && originalContactId && formData.customer.phone) {
          console.log('[AddOrderScreen] Phone number changed, adding to parent contact:', originalContactId);
          try {
            // Update parent contact to add new phone identifier
            const updateResponse = await apiService.updateParentContact(originalContactId, {
              identifiers: {
                phone: [formData.customer.phone], // Backend expects arrays
              },
              profile: {
                phone: formData.customer.phone,
              },
            });
            
            if (updateResponse.statusCode === 1000) {
              console.log('[AddOrderScreen] Successfully added new phone to parent contact');
            } else {
              console.warn('[AddOrderScreen] Failed to add phone to parent contact:', updateResponse.errors);
            }
          } catch (error) {
            console.error('[AddOrderScreen] Error adding phone to parent contact:', error);
          }
          finalContactId = originalContactId; // Keep same parent contact
        } else if (originalContactId) {
          // Contact unchanged, use original
          finalContactId = originalContactId;
        }

        // Convert notes array to format expected by API
        // Ensure notes is properly formatted as array
        let notesForApi: string | Note[];
        if (Array.isArray(formData.notes)) {
          // Filter out empty notes and ensure proper format
          const validNotes = formData.notes.filter(note => note && note.content && note.content.trim());
          console.log('[AddOrderScreen] Edit mode - Notes array:', formData.notes, 'Valid notes:', validNotes);
          if (validNotes.length > 0) {
            // Convert to JSON string for API
            notesForApi = JSON.stringify(validNotes);
            console.log('[AddOrderScreen] Edit mode - Notes JSON:', notesForApi);
          } else {
            notesForApi = '';
            console.log('[AddOrderScreen] Edit mode - No valid notes, using empty string');
          }
        } else if (formData.notes && typeof formData.notes === 'string') {
          notesForApi = formData.notes;
          console.log('[AddOrderScreen] Edit mode - Notes as string:', notesForApi);
        } else {
          notesForApi = '';
          console.log('[AddOrderScreen] Edit mode - Notes is empty or invalid, using empty string');
        }

        // Prepare update data with all modified fields
        const updateData: any = {
          orderStatus: formData.orderStatus,
          paymentStatus: formData.paymentStatus,
          shippingAddress: formData.shippingAddress,
          shippingLocation: formData.shippingLocation,
          shippingMethod: formData.shippingMethod,
          shippingMethodId: formData.shippingMethodId,
          shippingMethodSnapshot: formData.shippingMethodSnapshot,
          trackingNumber: formData.trackingNumber,
          notes: notesForApi,
          // Include customer data if changed
          customer: {
            name: formData.customer.name,
            phone: formData.customer.phone,
            email: Array.isArray(formData.customer.email)
              ? (formData.customer.email[0] || undefined)
              : (formData.customer.email || undefined),
            address: formData.customer.address,
            location: formData.customer.location,
          },
          // Include items — strip client-only product.addons before sending to API
          items: formData.items.map(({ product: { addons: _addons, inventoryTracking: _it, inventoryQuantity: _iq, vendorIds: _vendorIds, ...product }, ...item }) => ({ ...item, product })),
          // Include pricing if modified
          subtotal: formData.subtotal,
          tax: formData.tax,
          extraCharges: (formData.extraCharges ?? []).filter((c: any) => !c._optional || optionalCharges.some(o => o.name === c.name && o.checked)),
          shipping: formData.shipping,
          discount: formData.discount,
          couponCode: appliedCoupon?.code,
          selectedChargeNames: optionalCharges.filter(c => c.checked).map(c => c.name),
          totalPrice: formData.totalPrice,
          currency: formData.currency,
        };

        // Update contactId if changed
        if (finalContactId) {
          updateData.contactId = finalContactId;
        }

        if (allowNegativeStock) {
          updateData.allowNegativeStock = true;
        }

        const response = await apiService.updateOrder(orderId, updateData);

        if (response.statusCode === 1000 && response.data) {
          showNotification('success', t('orders.common.success'), t('orders.form.updated'));
          setTimeout(() => {
            if (listId) {
              navigateTo('data-list-records', listId);
            } else if (Platform.OS === 'web') {
              window.history.back();
            } else {
              navigateTo('orders');
            }
          }, 1500);
        } else {
          const errorMessage =
            response.errors?.[0]?.message || 'Failed to update order';
          showNotification('error', t('orders.common.error'), errorMessage);
        }
      }
    } catch (error: any) {
      console.error(`Error ${mode === 'new' ? 'creating' : 'updating'} order:`, error);
      const errorMessage = error.message || 
                          error.errors?.[0]?.message || 
                          `Failed to ${mode === 'new' ? 'create' : 'update'} order`;
      showNotification('error', t('orders.common.error'), errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (listId) {
      navigateTo('data-list-records', listId);
    } else if (Platform.OS === 'web') {
      window.history.back();
    } else {
      navigateTo('orders');
    }
  };

  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  const updateField = <K extends keyof OrderFormData>(
    field: K,
    value: OrderFormData[K]
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field as string]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field as string];
        return newErrors;
      });
    }
  };

  const updateCustomerField = <K extends keyof OrderFormData['customer']>(
    field: K,
    value: OrderFormData['customer'][K]
  ) => {
    setFormData((prev) => ({
      ...prev,
      customer: { ...prev.customer, [field]: value }
    }));
    // Clear error for this field
    if (errors[`customer${field.charAt(0).toUpperCase() + field.slice(1)}`]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[`customer${field.charAt(0).toUpperCase() + field.slice(1)}`];
        return newErrors;
      });
    }
  };

  const getStatusColor = (status: string): string => {
    const statusColors: Record<string, string> = {
      pending: '#FFA500',
      placed: '#4A90E2',
      confirmed: '#50C878',
      processing: '#9370DB',
      shipped: '#FF6B6B',
      delivered: '#32CD32',
      cancelled: '#DC143C',
    };
    return statusColors[status] || String(colors.primary);
  };

  const getPaymentStatusColor = (status: string): string => {
    const statusColors: Record<string, string> = {
      unpaid: '#DC143C',
      partial: '#FFA500',
      paid: '#32CD32',
      refunded: '#808080',
    };
    return statusColors[status] || String(colors.primary);
  };

  // Dropdown options
  const orderStatusOptions = [
    { value: 'pending', label: t('orders.status.pending') },
    { value: 'placed', label: t('orders.status.placed') },
    { value: 'confirmed', label: t('orders.status.confirmed') },
    { value: 'processing', label: t('orders.status.processing') },
    { value: 'shipped', label: t('orders.status.shipped') },
    { value: 'delivered', label: t('orders.status.delivered') },
    { value: 'cancelled', label: t('orders.status.cancelled') },
  ];

  const paymentStatusOptions = [
    { value: 'unpaid', label: t('orders.payment.unpaid') },
    { value: 'partial', label: t('orders.payment.partial') },
    { value: 'paid', label: t('orders.payment.paid') },
    { value: 'refunded', label: t('orders.payment.refunded') },
  ];

  // Show loading state for edit mode while fetching order
  if (mode === 'edit' && loadingOrder) {
    return (
      <ScreenLayout
        header={
          <Header
            showNotifications={true}
            showMessages={true}
            onNotificationPress={() => navigateTo('notifications')}
            onMessagePress={() => navigateTo('chat')}
            showNavigation={Platform.OS === 'web'}
            activeTab={activeTab}
            onTabPress={handleTabPress}
          />
        }
        showBottomTabs={Platform.OS !== 'web'}
        onLeftPaneClose={() => {}}
        leftPaneContent="settings"
        onSettingsItemPress={async (itemId) => {
          const { setPendingSettingsItem } = await import('../utils/tempStorage');
          await setPendingSettingsItem(itemId);
          navigateTo('settings');
        }}
        onRightPaneClose={() => {}}
      >
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={styles.loadingText}>{t('orders.form.loading')}</Text>
        </View>
      </ScreenLayout>
    );
  }

  // Validate required props
  if (mode === 'edit' && !orderId) {
    return (
      <ScreenLayout
        header={
          <Header
            showNotifications={true}
            showMessages={true}
            onNotificationPress={() => navigateTo('notifications')}
            onMessagePress={() => navigateTo('chat')}
            showNavigation={Platform.OS === 'web'}
            activeTab={activeTab}
            onTabPress={handleTabPress}
          />
        }
        showBottomTabs={Platform.OS !== 'web'}
      >
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>{t('orders.form.orderIdRequired')}</Text>
        </View>
      </ScreenLayout>
    );
  }

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications={true}
          showMessages={true}
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      onRightPaneClose={() => {}}
      formHeader={
        <FormHeader
          title={mode === 'edit' && orderNumber ? orderNumber : mode === 'edit' ? t('orders.form.editTitle') : t('orders.form.newTitle')}
          onBack={handleCancel}
          actions={[
            ...(mode === 'edit' && orderNumber ? [{
              icon: 'copy-outline' as const,
              onPress: async () => {
                try {
                  if (Platform.OS === 'web' && navigator.clipboard) {
                    await navigator.clipboard.writeText(orderNumber);
                  } else {
                    await Clipboard.setStringAsync(orderNumber);
                  }
                  showNotification('success', t('orders.msg.copied'), t('orders.msg.numberCopied'));
                } catch (error) {
                  console.error('Failed to copy order number:', error);
                  showNotification('error', t('orders.common.error'), t('orders.msg.copyFailed'));
                }
              },
              accessibilityLabel: t('orders.form.copyNumber'),
            }] : []),
            ...(mode === 'edit' && orderId ? [{
              icon: 'documents-outline' as const,
              onPress: () => setShowDocuments(true),
              label: t('documents.title'),
              accessibilityLabel: t('documents.title'),
            }, {
              icon: 'print-outline' as const,
              onPress: () => setShowPackingSlips(true),
              label: t('orders.form.packingSlips'),
              accessibilityLabel: t('orders.form.packingSlips'),
            }] : []),
            { icon: 'checkmark', primary: true, onPress: handleSubmit, loading, disabled: loading },
          ]}
        />
      }
    >
      <View style={styles.container}>
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={[
            styles.contentContainer,
            Platform.OS === 'web' && styles.contentContainerWeb,
            Platform.OS !== 'web' && styles.contentContainerMobile,
            phoneFormGutter,
          ]}
          showsVerticalScrollIndicator={false}
        >

        {/* Linked quotation — this order was created by converting a quotation,
            which preserved that quotation's exact prices (see quotationId on
            the Orders entity / QuotationService.convertToOrder). */}
        {mode === 'edit' && quotationId && (
          <TouchableOpacity
            style={styles.quotationBanner}
            onPress={() => navigateTo('quotation-detail', quotationId)}
            activeOpacity={0.7}
          >
            <Ionicons name="document-text-outline" size={18} color={colors.primary} />
            <Text style={styles.quotationBannerText}>{t('orders.form.viewQuotation')}</Text>
            <DirectionalIcon name="chevron-forward" size={16} color={colors.primary} />
          </TouchableOpacity>
        )}

        {/* Customer Information */}
        <View style={[styles.section, styles.customerSection]}>
          <Text style={styles.sectionTitle}>{t('orders.form.customerInfo')}</Text>

          <View style={styles.contactSelectorContainer}>
            <ContactSelector
              mode="edit"
              variant="simple"
              contact={selectedContact}
              onContactSelect={handleContactSelect}
              onContactUpdate={handleContactUpdate}
              onAddNewContact={() => navigateTo('contacts')}
              showPreviousEvents={false}
              showPreviousOrders={false}
            />
            {errors.customerName && (
              <Text style={styles.errorText}>{errors.customerName}</Text>
            )}
          </View>
        </View>

        {/* Order Status & Payment Status */}
        <View style={[
          styles.section,
          (orderStatusDropdownOpen || paymentStatusDropdownOpen) && styles.sectionWithDropdown
        ]}>
          <View style={styles.inlineStatusRow}>
            <View style={styles.inlineStatusColumn}>
              <Dropdown
                label={t('orders.field.orderStatus')}
                value={formData.orderStatus}
                options={orderStatusOptions}
                isOpen={orderStatusDropdownOpen}
                onToggle={() => {
                  setOrderStatusDropdownOpen(!orderStatusDropdownOpen);
                  setPaymentStatusDropdownOpen(false);
                }}
                onSelect={(value) => {
                  updateField('orderStatus', value as any);
                  setOrderStatusDropdownOpen(false);
                }}
                placeholder={t('orders.form.selectStatus')}
                style={styles.statusDropdown}
                wrapperStyle={[
                  styles.statusDropdownWrapper,
                  orderStatusDropdownOpen && styles.statusDropdownWrapperOpen
                ]}
              />
            </View>

            <View style={styles.inlineStatusColumn}>
              <Dropdown
                label={t('orders.field.paymentStatus')}
                value={formData.paymentStatus || 'unpaid'}
                options={paymentStatusOptions}
                isOpen={paymentStatusDropdownOpen}
                onToggle={() => {
                  setPaymentStatusDropdownOpen(!paymentStatusDropdownOpen);
                  setOrderStatusDropdownOpen(false);
                }}
                onSelect={(value) => {
                  updateField('paymentStatus', value as any);
                  setPaymentStatusDropdownOpen(false);
                }}
                placeholder={t('orders.form.selectPaymentStatus')}
                style={styles.statusDropdown}
                wrapperStyle={[
                  styles.statusDropdownWrapper,
                  paymentStatusDropdownOpen && styles.statusDropdownWrapperOpen
                ]}
              />
            </View>
          </View>
        </View>

        {/* Shipping Information */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>{t('orders.form.shippingInfo')}</Text>

          {/* Address Text */}
          <View style={styles.inputContainer}>
            <Text style={styles.inputLabel}>{t('orders.form.shippingAddress')}</Text>
            <Input
              value={formData.shippingAddress || ''}
              onChangeText={(text) => updateField('shippingAddress', text)}
              placeholder={t('orders.form.enterShippingAddress')}
              multiline
              numberOfLines={3}
            />
          </View>

          {/* Location Picker */}
          <View style={styles.inputContainer}>
            <Text style={styles.inputLabel}>{t('orders.form.locationOptional')}</Text>
            <TouchableOpacity
              style={styles.locationButton}
              onPress={() => setShowLocationPicker(true)}
            >
              {formData.shippingLocation ? (
                <View style={styles.locationInfo}>
                  <Ionicons name="location" size={20} color={colors.primary} />
                  <View style={styles.locationTextContainer}>
                    <Text style={styles.locationAddress} numberOfLines={1}>
                      {formData.shippingLocation.address}
                    </Text>
                    <Text style={styles.locationCoordinates}>
                      {formData.shippingLocation.latitude.toFixed(6)}, {formData.shippingLocation.longitude.toFixed(6)}
                    </Text>
                  </View>
                  <DirectionalIcon name="chevron-forward" size={20} color={colors.textLight} />
                </View>
              ) : (
                <View style={styles.locationPlaceholder}>
                  <Ionicons name="location-outline" size={20} color={colors.textLight} />
                  <Text style={styles.locationPlaceholderText}>{t('orders.form.selectLocation')}</Text>
                  <DirectionalIcon name="chevron-forward" size={20} color={colors.textLight} />
                </View>
              )}
            </TouchableOpacity>
          </View>

          {/* Shipping Method Selector */}
          <Text style={styles.inputLabel}>{t('orders.form.shippingMethod')}</Text>
          {formData.shippingMethodId ? (
            <View>
              <View style={styles.shippingMethodSelected}>
                <View style={styles.shippingMethodInfo}>
                  <Text style={styles.shippingMethodName}>{formData.shippingMethod}</Text>
                  <Text style={styles.shippingMethodPrice}>
                    {formData.shippingMethodSnapshot?.price === 0
                      ? t('orders.form.free')
                      : `${formData.currency} ${(formData.shippingMethodSnapshot?.price || 0).toFixed(2)}`}
                  </Text>
                </View>
                <View style={styles.shippingMethodActions}>
                  <TouchableOpacity onPress={() => { loadShippingMethods(); setShowShippingMethodModal(true); }} style={styles.shippingMethodBtn}>
                    <Ionicons name="swap-horizontal-outline" size={18} color={colors.primary} />
                  </TouchableOpacity>
                  <TouchableOpacity onPress={handleClearShippingMethod} style={styles.shippingMethodBtn}>
                    <Ionicons name="close-circle-outline" size={18} color={colors.error} />
                  </TouchableOpacity>
                </View>
              </View>
              {/* Zone-based: show matched zone name */}
              {selectedShippingMethod?.type === 'location_based' && selectedShippingMethod?._zoneMatched && (
                <View style={styles.thresholdWarning}>
                  <Ionicons name="location-outline" size={14} color={colors.primary} />
                  <Text style={[styles.thresholdWarningText, { color: colors.primary }]}>
                    {selectedShippingMethod._resolvedZone
                      ? `Zone: ${selectedShippingMethod._resolvedZone.name}`
                      : 'Other area (fallback rate)'}
                  </Text>
                </View>
              )}
              {/* Free shipping threshold warning */}
              {isFreeThresholdNotMet(selectedShippingMethod, formData.subtotal) && (
                <View style={styles.thresholdWarning}>
                  <Ionicons name="information-circle-outline" size={14} color="#FF9500" />
                  <Text style={styles.thresholdWarningText}>
                    Add {formData.currency} {((selectedShippingMethod.minOrderAmount - formData.subtotal) || 0).toFixed(2)} more to qualify for free delivery
                  </Text>
                </View>
              )}
              {/* Express option toggle */}
              {selectedShippingMethod?.expressOption?.enabled && (
                <TouchableOpacity
                  style={[styles.expressToggle, expressSelected && styles.expressToggleActive]}
                  onPress={handleToggleExpress}
                  activeOpacity={0.7}
                >
                  <Ionicons
                    name="flash-outline"
                    size={15}
                    color={expressSelected ? colors.white : colors.primary}
                  />
                  <Text style={[styles.expressToggleText, expressSelected && styles.expressToggleTextActive]}>
                    {selectedShippingMethod.expressOption.label || 'Express'}
                    {' +'}
                    {formData.currency} {(selectedShippingMethod.expressOption.extraCost || 0).toFixed(2)}
                  </Text>
                  {expressSelected && (
                    <Ionicons name="checkmark" size={14} color={colors.white} />
                  )}
                </TouchableOpacity>
              )}
            </View>
          ) : (
            <TouchableOpacity
              style={styles.shippingMethodPicker}
              onPress={() => { loadShippingMethods(); setShowShippingMethodModal(true); }}
            >
              <Ionicons name="car-outline" size={18} color={colors.textLight} />
              <Text style={styles.shippingMethodPlaceholder}>{t('orders.form.selectShippingMethod')}</Text>
              <Ionicons name="chevron-down" size={16} color={colors.textLight} />
            </TouchableOpacity>
          )}
          {errors.shippingMethod && (
            <Text style={styles.shippingMethodError}>{errors.shippingMethod}</Text>
          )}

          <Text style={[styles.inputLabel, { marginTop: spacing.sm }]}>{t('orders.form.trackingNumber')}</Text>
          <Input
            value={formData.trackingNumber || ''}
            onChangeText={(text) => updateField('trackingNumber', text)}
            placeholder={t('orders.form.enterTrackingNumber')}
          />
        </View>

        {/* Order Items */}
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionTitle} numberOfLines={1}>{t('orders.form.items')}</Text>
            {canEditItems() && (
              <TouchableOpacity
                style={styles.addItemButton}
                onPress={handleAddProduct}
                activeOpacity={0.7}
              >
                <Ionicons name="add-circle" size={16} color={colors.primary} />
                <Text style={styles.addItemButtonText} numberOfLines={1}>{t('orders.form.add')}</Text>
              </TouchableOpacity>
            )}
          </View>
          
          <View style={{ flexDirection: rtl.rowDirection, alignItems: 'center', gap: spacing.sm, marginBottom: spacing.sm }}>
            <View style={{ width: 160 }}>
              <CurrencyPicker
                label={t('orders.form.currency')}
                value={formData.currency}
                onChange={(code) => { if (canEditItems() && !changingCurrency) handleCurrencyChange(code); }}
              />
            </View>
            {changingCurrency ? <ActivityIndicator size="small" color={colors.primary} /> : null}
          </View>
          {canEditItems() && <OrderMinimumsNotice report={minimums} style={{ marginBottom: spacing.sm }} />}
          {formData.items.length > 0 ? (
            <>
              {formData.items.map((item, index) => {
                const typeConfig: Record<string, { label: string; color: string }> = {
                  physical: { label: t('orders.form.typePhysical'), color: '#4A90E2' },
                  digital: { label: t('orders.form.typeDigital'), color: '#7B68EE' },
                  service: { label: t('orders.form.typeService'), color: '#50C878' },
                };
                const typeInfo = item.product.productType ? typeConfig[item.product.productType] : null;
                const unitLabel = item.product.priceUnit ? ` / ${item.product.priceUnit}` : '';

                return (
                <View key={index} style={styles.itemRow}>
                  <View style={styles.itemInfo}>
                    <View style={styles.itemNameRow}>
                      <Text style={[styles.itemName, { textAlign: rtl.textStart }]}>{item.product.name}</Text>
                      {typeInfo && (
                        <View style={[styles.itemTypeBadge, { backgroundColor: withAlpha(typeInfo.color, 0.13) }]}>
                          <Text style={[styles.itemTypeBadgeText, { color: typeInfo.color }]}>{typeInfo.label}</Text>
                        </View>
                      )}
                    </View>
                    {item.product.brand && (
                      <Text style={styles.itemBrand}>{item.product.brand}</Text>
                    )}
                    {(() => {
                      const vendorOptions = [...new Set([...(item.product.vendorIds || []), ...(item.vendorId ? [item.vendorId] : [])])];
                      if (!vendorOptions.length) return null;
                      const current = item.vendorId || vendorOptions[0];
                      const name = (id: string) => vendorNames[id] || (id === item.vendorId ? item.vendorName : '') || '…';
                      return (
                        <View style={[styles.itemVendorRow, { flexDirection: rtl.rowDirection }]} accessibilityLabel={t('orders.form.lineVendor')}>
                          <Ionicons name="business-outline" size={12} color={colors.textLight} />
                          {vendorOptions.length > 1 && canEditItems()
                            ? vendorOptions.map((id) => (
                              <TouchableOpacity key={id} style={[styles.itemVendorChip, current === id && styles.itemVendorChipActive]} onPress={() => setItemVendor(index, id)}>
                                <Text style={[styles.itemVendorText, current === id && styles.itemVendorTextActive]} numberOfLines={1}>{name(id)}</Text>
                              </TouchableOpacity>
                            ))
                            : <Text style={styles.itemVendorText} numberOfLines={1}>{name(current)}</Text>}
                        </View>
                      );
                    })()}
                    {canEditItems() ? (
                      <View style={styles.itemQuantityControls}>
                        <TouchableOpacity
                          style={styles.quantityButton}
                          onPress={() => updateItemQuantity(index, item.quantity - 1)}
                          activeOpacity={0.7}
                        >
                          <Ionicons name="remove-circle" size={20} color={colors.primary} />
                        </TouchableOpacity>
                        <TextInput
                          style={styles.quantityInput}
                          value={item.quantity.toString()}
                          onChangeText={(text) => {
                            const quantity = parseInt(text) || 0;
                            updateItemQuantity(index, quantity);
                          }}
                          keyboardType="numeric"
                          selectTextOnFocus
                        />
                        <TouchableOpacity
                          style={styles.quantityButton}
                          onPress={() => updateItemQuantity(index, item.quantity + 1)}
                          activeOpacity={0.7}
                        >
                          <Ionicons name="add-circle" size={20} color={colors.primary} />
                        </TouchableOpacity>
                        <Text style={styles.itemUnitPrice}>
                          × {item.product.currency || formData.currency} {item.unitPrice.toFixed(2)}{unitLabel}
                        </Text>
                      </View>
                    ) : (
                      <Text style={styles.itemDetails}>
                        Qty: {item.quantity} × {item.product.currency || formData.currency} {item.unitPrice.toFixed(2)}{unitLabel}
                      </Text>
                    )}

                    {/* Addons */}
                    {item.product.addons && item.product.addons.length > 0 && canEditItems() && (
                      <View style={styles.addonsSection}>
                        <Text style={styles.addonsSectionLabel}>{t('orders.form.addons')}</Text>
                        <View style={styles.addonsList}>
                          {item.product.addons.map((addon) => {
                            const selected = (item.selectedAddons || []).find(a => a.addonId === addon.id);
                            const isSelected = !!selected;
                            return (
                              <View key={addon.id} style={[styles.addonChip, isSelected && styles.addonChipSelected]}>
                                {isSelected ? (
                                  // Expanded: show qty controls + remove
                                  <>
                                    <TouchableOpacity
                                      onPress={() => updateAddonQuantity(index, addon.id, -1, addon.maxQuantity)}
                                      hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}
                                    >
                                      <Ionicons name="remove-circle" size={16} color={colors.primary} />
                                    </TouchableOpacity>
                                    <Text style={styles.addonQty}>{selected.quantity}</Text>
                                    <TouchableOpacity
                                      onPress={() => updateAddonQuantity(index, addon.id, 1, addon.maxQuantity)}
                                      hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}
                                    >
                                      <Ionicons name="add-circle" size={16} color={colors.primary} />
                                    </TouchableOpacity>
                                    <Text style={styles.addonChipTextSelected}>
                                      {addon.name}{addon.isRequired ? '*' : ''} +{selected.currency || item.product.currency || formData.currency} {(addon.price * selected.quantity).toFixed(2)}
                                    </Text>
                                    <TouchableOpacity
                                      onPress={() => toggleItemAddon(index, addon)}
                                      hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}
                                      style={styles.addonRemove}
                                    >
                                      <Ionicons name="close-circle" size={14} color={colors.primary} />
                                    </TouchableOpacity>
                                  </>
                                ) : (
                                  // Collapsed: tap to add
                                  <TouchableOpacity
                                    style={styles.addonChipInner}
                                    onPress={() => toggleItemAddon(index, addon)}
                                    activeOpacity={0.7}
                                  >
                                    <Ionicons name="add" size={12} color={colors.textSecondary} />
                                    <Text style={styles.addonChipText}>
                                      {addon.name}{addon.isRequired ? '*' : ''} +{addon.currency || item.product.currency || formData.currency} {addon.price.toFixed(2)}
                                    </Text>
                                  </TouchableOpacity>
                                )}
                              </View>
                            );
                          })}
                        </View>
                      </View>
                    )}

                    {/* Selected addons summary (read-only) */}
                    {item.selectedAddons && item.selectedAddons.length > 0 && !canEditItems() && (
                      <View style={styles.addonsSection}>
                        {item.selectedAddons.map((a) => (
                          <Text key={a.addonId} style={styles.addonReadOnly}>
                            + {a.name} ×{a.quantity} ({item.product.currency || formData.currency} {(a.price * a.quantity).toFixed(2)})
                          </Text>
                        ))}
                      </View>
                    )}
                  </View>
                  <View style={styles.itemRight}>
                    <Text style={styles.itemTotal}>
                      {item.product.currency || formData.currency} {item.totalPrice.toFixed(2)}
                    </Text>
                    {canEditItems() && (
                      <TouchableOpacity
                        style={styles.removeItemButton}
                        onPress={() => {
                          if (Platform.OS === 'web') {
                            if (window.confirm(`Remove ${item.product.name} from order?`)) {
                              removeItem(index);
                            }
                          } else {
                            Alert.alert(
                              'Remove Item',
                              `Are you sure you want to remove ${item.product.name}?`,
                              [
                                { text: 'Cancel', style: 'cancel' },
                                { text: 'Remove', style: 'destructive', onPress: () => removeItem(index) },
                              ]
                            );
                          }
                        }}
                        activeOpacity={0.7}
                      >
                        <Ionicons name="close-circle" size={20} color={colors.error} />
                      </TouchableOpacity>
                    )}
                  </View>
                </View>
                );
              })}
              {/* Coupon Code */}
              {(
                <View style={styles.promoRow}>
                  <View style={styles.promoInputWrap}>
                    <Ionicons name="pricetag-outline" size={16} color={colors.textLight} style={rtl.marginEnd(4)} />
                    <TextInput
                      style={styles.promoInput}
                      value={couponCode}
                      onChangeText={setCouponCode}
                      placeholder={t('orders.form.couponCode')}
                      placeholderTextColor={colors.textLight}
                      autoCapitalize="characters"
                      editable={!appliedCoupon}
                    />
                    {appliedCoupon ? (
                      <TouchableOpacity onPress={() => { setAppliedCoupon(null); applyDiscount(0); setCouponCode(''); }}>
                        <Ionicons name="close-circle" size={18} color={colors.error} />
                      </TouchableOpacity>
                    ) : (
                      <TouchableOpacity onPress={applyCouponCode} disabled={validatingCoupon || !couponCode.trim()}>
                        {validatingCoupon
                          ? <ActivityIndicator size="small" color={colors.primary} />
                          : <Text style={styles.applyBtnText}>{t('orders.form.apply')}</Text>}
                      </TouchableOpacity>
                    )}
                  </View>
                </View>
              )}
              {/* Subtotal */}
              <View style={styles.totalRow}>
                <Text style={styles.totalLabel}>{t('orders.form.subtotal')}</Text>
                <Text style={styles.totalValue}>{formData.currency} {formData.subtotal.toFixed(2)}</Text>
              </View>

              {/* Extra charges (taxes + fees) — each line itemised */}
              {loadingCharges ? (
                <View style={styles.totalRow}>
                  <Text style={styles.totalLabel}>Calculating taxes & charges…</Text>
                  <ActivityIndicator size="small" color={colors.primary} />
                </View>
              ) : (
                <>
                  {/* Mandatory charges — always shown */}
                  {(formData.extraCharges ?? []).filter(c => !c._optional).map((charge, i) => (
                    <View key={i} style={styles.totalRow}>
                      <Text style={styles.totalLabel}>{charge.name}</Text>
                      <Text style={styles.totalValue}>+{formData.currency} {charge.amount.toFixed(2)}</Text>
                    </View>
                  ))}
                  {/* Optional charges — toggleable rows */}
                  {optionalCharges.map((charge, i) => (
                    <TouchableOpacity
                      key={`opt-${i}`}
                      style={[styles.totalRow, { opacity: charge.checked ? 1 : 0.6 }]}
                      onPress={() => toggleOptionalCharge(charge.name, !charge.checked, charge.amount)}
                      activeOpacity={0.7}
                    >
                      <View style={{ flexDirection: rtl.rowDirection, alignItems: 'center', flex: 1, gap: 8 }}>
                        <View style={{
                          width: 18, height: 18, borderRadius: 4, borderWidth: 1.5,
                          borderColor: charge.checked ? colors.primary : colors.border,
                          backgroundColor: charge.checked ? colors.primary : 'transparent',
                          alignItems: 'center', justifyContent: 'center',
                        }}>
                          {charge.checked && <Ionicons name="checkmark" size={12} color="#fff" />}
                        </View>
                        <Text style={styles.totalLabel}>{charge.name} <Text style={{ color: colors.textLight, fontSize: 11 }}>(optional)</Text></Text>
                      </View>
                      <Text style={[styles.totalValue, !charge.checked && { color: colors.textLight }]}>
                        +{formData.currency} {charge.amount.toFixed(2)}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </>
              )}

              {/* Delivery */}
              <View style={styles.totalRow}>
                <View style={{ flex: 1 }}>
                  <Text style={styles.totalLabel}>
                    {t('orders.form.delivery')}{formData.shippingMethod ? ` — ${formData.shippingMethod}` : ''}
                  </Text>
                  {isFreeThresholdNotMet(selectedShippingMethod, formData.subtotal) && (
                    <Text style={styles.thresholdHint}>
                      {t('orders.form.freeAbove', { currency: formData.currency, amount: (selectedShippingMethod.minOrderAmount || 0).toFixed(2) })}
                    </Text>
                  )}
                </View>
                <Text style={[styles.totalValue, (formData.shipping || 0) === 0 && formData.shippingMethodId ? styles.totalValueFree : null]}>
                  {(formData.shipping || 0) === 0 && formData.shippingMethodId
                    ? t('orders.form.free')
                    : (formData.shipping || 0) > 0
                    ? `${formData.currency} ${(formData.shipping || 0).toFixed(2)}`
                    : '—'}
                </Text>
              </View>

              {/* Manual discount row */}
              {(
                <View style={styles.promoRow}>
                  <View style={styles.promoInputWrap}>
                    <Ionicons name="cut-outline" size={16} color={colors.textLight} style={rtl.marginEnd(4)} />
                    <TextInput
                      style={styles.promoInput}
                      value={formData.discountReason ?? ''}
                      onChangeText={v => setFormData(p => ({ ...p, discountReason: v }))}
                      placeholder={t('orders.form.discountReason')}
                      placeholderTextColor={colors.textLight}
                    />
                    <TextInput
                      style={[styles.promoInput, { width: 70, textAlign: 'right' }]}
                      value={formData.manualDiscount ? String(formData.manualDiscount) : ''}
                      onChangeText={v => {
                        const amount = parseFloat(v) || 0;
                        const couponDiscount = appliedCoupon?.discountAmount ?? 0;
                        setFormData(p => {
                          const chargesTotal = (p.extraCharges ?? []).reduce((s, c) => s + c.amount, 0);
                          const totalDiscount = amount + couponDiscount;
                          const totalPrice = Math.max(0, p.subtotal + (p.tax || 0) + (p.shipping || 0) + chargesTotal - totalDiscount);
                          return { ...p, manualDiscount: amount, discount: totalDiscount, totalPrice };
                        });
                      }}
                      keyboardType="decimal-pad"
                      placeholder="0.00"
                      placeholderTextColor={colors.textLight}
                    />
                  </View>
                </View>
              )}

              {/* Total discount line */}
              {(formData.discount || 0) > 0 && (
                <View style={styles.totalRow}>
                  <Text style={styles.totalLabel}>
                    {t('orders.form.discount')}{appliedCoupon ? ` (${appliedCoupon.code})` : ''}
                  </Text>
                  <Text style={[styles.totalValue, { color: colors.success }]}>
                    -{formData.currency} {(formData.discount || 0).toFixed(2)}
                  </Text>
                </View>
              )}

              <View style={[styles.totalRow, styles.finalTotalRow]}>
                <Text style={styles.finalTotalLabel}>{t('orders.form.total')}</Text>
                <Text style={styles.finalTotalValue}>{formData.currency} {formData.totalPrice.toFixed(2)}</Text>
              </View>
            </>
          ) : (
            <View style={styles.emptyItemsContainer}>
              <Text style={styles.emptyItemsText}>{t('orders.form.noItems')}</Text>
              {canEditItems() && (
                <TouchableOpacity
                  style={styles.addItemButtonLarge}
                  onPress={handleAddProduct}
                  activeOpacity={0.7}
                >
                  <Ionicons name="add-circle" size={24} color={colors.primary} />
                  <Text style={styles.addItemButtonTextLarge}>{t('orders.form.addItems')}</Text>
                </TouchableOpacity>
              )}
            </View>
          )}
        </View>

        {/* Notes */}
        <View style={styles.section}>
          <NotesSection
            notes={(() => {
              // Ensure notes is always an array of Note objects
              if (!formData.notes) return [];
              if (Array.isArray(formData.notes)) {
                return formData.notes.map((note: any) => {
                  // If note is a string (shouldn't happen, but handle it), convert to Note object
                  if (typeof note === 'string') {
                    return {
                      content: note,
                      createdBy: { id: '', email: '' },
                      createdAt: new Date(),
                    };
                  }
                  // Ensure createdAt is a Date object
                  if (note && typeof note === 'object') {
                    return {
                      ...note,
                      createdAt: note.createdAt ? (typeof note.createdAt === 'string' ? new Date(note.createdAt) : note.createdAt) : new Date(),
                    };
                  }
                  return note;
                });
              }
              // If notes is a JSON string, parse it
              if (typeof formData.notes === 'string') {
                try {
                  const parsed = JSON.parse(formData.notes);
                  if (Array.isArray(parsed)) {
                    return parsed.map((note: any) => ({
                      ...note,
                      createdAt: note.createdAt ? (typeof note.createdAt === 'string' ? new Date(note.createdAt) : new Date(note.createdAt)) : new Date(),
                    }));
                  }
                } catch (e) {
                  // If parsing fails, treat as plain string
                  return [{
                    content: formData.notes,
                    createdBy: { id: '', email: '' },
                    createdAt: new Date(),
                  }];
                }
              }
              return [];
            })()}
            onAddNote={(content) => {
              if (!content || !content.trim()) {
                console.warn('[AddOrderScreen] Attempted to add empty note');
                return;
              }
              const newNote: Note = {
                content: content.trim(),
                createdBy: {
                  id: user?.id || '',
                  _id: user?.id || '',
                  firstName: user?.firstName,
                  lastName: user?.lastName,
                  email: user?.email,
                  avatar: user?.avatar,
                  profilePicture: user?.profilePicture,
                },
                createdAt: new Date(),
              };
              const currentNotes = Array.isArray(formData.notes) ? formData.notes : [];
              const updatedNotes = [...currentNotes, newNote];
              console.log('[AddOrderScreen] Adding note:', newNote, 'Total notes:', updatedNotes.length);
              setFormData(prev => ({
                ...prev,
                notes: updatedNotes,
              }));
            }}
            onDeleteNote={(noteId) => {
              const currentNotes = Array.isArray(formData.notes) ? formData.notes : [];
              setFormData(prev => ({
                ...prev,
                notes: currentNotes.filter((note, index) => {
                  // If noteId is a string, match by _id
                  // If noteId is a number, match by index
                  if (typeof noteId === 'string') {
                    return note._id !== noteId;
                  }
                  // If noteId is a number (index), filter by index
                  return index !== noteId;
                }),
              }));
            }}
            placeholder={t('orders.form.notePlaceholder')}
            title={t('orders.form.notes')}
            editable={true}
            currentUserId={user?.id}
          />

          {/* Extra Fields (item 2) — custom columns on the linked system
              Orders Data List, alongside this order's own fixed fields
              above. Edit mode only (see the state-declaration comment). */}
          {listId && mode === 'edit' && customColumns.length > 0 && currentBusiness?._id && (
            <View style={styles.customFieldsSection}>
              <Text style={styles.customFieldsSectionTitle}>{t('orders.form.extraFields')}</Text>
              {customColumns.map((col) => (
                <DataListFieldRenderer
                  key={col.id}
                  column={col}
                  value={customValues[col.id]}
                  onChange={(v) => setCustomValues((prev) => ({ ...prev, [col.id]: v }))}
                  error={customValueErrors[col.id]}
                  businessId={currentBusiness._id}
                  listId={listId}
                  recordId={orderId}
                />
              ))}
              <Button
                title={t('orders.form.saveExtraFields')}
                onPress={handleSaveCustomValues}
                loading={savingCustomValues}
                variant="secondary"
              />
            </View>
          )}
        </View>
        </ScrollView>
      </View>

      {/* Location Picker Modal */}
      <MapLocationPicker
        visible={showLocationPicker}
        onLocationSelect={async (selectedLocation) => {
          setFormData(prev => ({
            ...prev,
            shippingLocation: selectedLocation,
            shippingAddress: selectedLocation.address || prev.shippingAddress,
          }));
          setShowLocationPicker(false);

          // If a location_based shipping method is already selected, re-resolve
          // the zone against the new location automatically
          if (selectedShippingMethod?.type === 'location_based') {
            const customer = {
              location: { latitude: selectedLocation.latitude, longitude: selectedLocation.longitude, address: selectedLocation.address },
            };
            const resolved = await resolveZoneForMethod(selectedShippingMethod, customer);
            if (resolved._zoneMatched) {
              setSelectedShippingMethod(resolved);
              setFormData(prev => {
                const subtotal = prev.subtotal || 0;
                const price = resolved._resolvedPrice ?? 0;
                const express = expressSelected && resolved.expressOption?.enabled ? (resolved.expressOption.extraCost ?? 0) : 0;
                const finalPrice = price + express;
                const zoneSuffix = resolved._resolvedZone ? ` · ${resolved._resolvedZone.name}` : resolved._zoneFallback ? ' · Other' : '';
                return {
                  ...prev,
                  shippingMethod: resolved.name + zoneSuffix,
                  shippingMethodSnapshot: { ...prev.shippingMethodSnapshot!, price: finalPrice },
                  shipping: finalPrice,
                  totalPrice: subtotal + (prev.tax || 0) + finalPrice - (prev.discount || 0),
                };
              });
            } else {
              // Location is now outside all zones — clear the method
              showNotification('warning', t('orders.form.zoneUnavailable'), t('orders.form.zoneUnavailableLocation'));
              handleClearShippingMethod();
            }
          }

          // Re-resolve product prices for location-based pricing zones
          setFormData(prev => {
            if (prev.items.length === 0) return prev;
            repriceItemsForLocation(prev.items as OrderItem[], selectedLocation).then(repriced => {
              setFormData(p => ({ ...p, items: repriced }));
              recalculateTotals(repriced as OrderItem[], {
                latitude: selectedLocation.latitude,
                longitude: selectedLocation.longitude,
              });
            });
            return prev;
          });
        }}
        onClose={() => setShowLocationPicker(false)}
        initialLocation={formData.shippingLocation ?? undefined}
      />

      {/* Product Selector Modal */}
      <ProductSelectorModal
        visible={showProductSelector}
        onClose={() => setShowProductSelector(false)}
        onConfirm={handleProductsSelected}
        title={t('orders.form.addProductsTitle')}
        confirmText={addingProducts ? 'Adding...' : 'Add to Order'}
        allowMultiple={true}
      />

      {/* Stock warning modal */}
      {mode === 'edit' && orderId && (
        <DocumentModal
          sendSource={{ orderId }}
          visible={showDocuments}
          types={ORDER_DOCUMENT_TYPES}
          scopeLabel={t('documents.forOrder', { number: orderNumber })}
          recipient={{ name: formData.customer.name, phone: formData.customer.phone, email: formData.customer.email }}
          unavailable={['paid', 'partial'].includes(String(formData.paymentStatus)) ? {} : { receipt: t('documents.receiptNeedsPayment') }}
          onClose={() => setShowDocuments(false)}
          onGenerate={async (req) => {
            const res = await apiService.generateOrderDocument(orderId, req);
            if (res.statusCode !== 1000 || !res.data) throw new Error((res as any).message || res.errors?.[0]?.message || t('documents.failed'));
            return res.data;
          }}
        />
      )}

      {mode === 'edit' && orderId && (
        <PackingSlipModal
          sendSource={{ orderId }}
          visible={showPackingSlips}
          scopeLabel={t('orders.form.packingSlipsScope', { count: new Set(formData.items.map((i) => i.vendorId || i.product.vendorIds?.[0] || '')).size })}
          onClose={() => setShowPackingSlips(false)}
          onGenerate={async (options) => {
            const res = await apiService.generateOrderPackingSlips(orderId, options);
            if (res.statusCode !== 1000 || !res.data) throw new Error((res as any).message || t('vendors.slips.failed'));
            return res.data;
          }}
        />
      )}

      <ConfirmationModal
        visible={!!stockWarning}
        type="warning"
        title={t('orders.form.lowStockTitle')}
        message={stockWarning?.message ?? ''}
        confirmText={t('orders.form.proceedAnyway')}
        cancelText={t('orders.common.cancel')}
        onConfirm={() => {
          stockWarning?.resolve(true);
          setStockWarning(null);
        }}
        onClose={() => {
          stockWarning?.resolve(false);
          setStockWarning(null);
        }}
      />

      {/* Shipping Method Picker Modal */}
      <Modal
        visible={showShippingMethodModal}
        transparent
        animationType="slide"
        onRequestClose={() => setShowShippingMethodModal(false)}
      >
        <View style={Platform.OS === 'web' ? styles.shippingModalWebOverlay : styles.shippingModalNativeContainer}>
          {Platform.OS === 'web' ? (
            <TouchableOpacity
              style={styles.shippingModalBackdrop}
              activeOpacity={1}
              onPress={() => setShowShippingMethodModal(false)}
            />
          ) : (
            <TouchableOpacity
              style={styles.shippingModalNativeBackdrop}
              activeOpacity={1}
              onPress={() => setShowShippingMethodModal(false)}
            />
          )}
          <View style={Platform.OS === 'web' ? styles.shippingModalWebContainer : undefined}>
        <View style={styles.shippingModalSheet}>
          <View style={styles.shippingModalHeader}>
            <Text style={styles.shippingModalTitle}>{t('orders.form.selectShippingMethodTitle')}</Text>
            <TouchableOpacity onPress={() => setShowShippingMethodModal(false)}>
              <Ionicons name="close" size={22} color={colors.text.primary} />
            </TouchableOpacity>
          </View>
          {loadingShippingMethods ? (
            <ActivityIndicator style={{ marginTop: spacing.xl }} color={colors.primary} />
          ) : shippingMethods.length === 0 ? (
            <View style={styles.shippingModalEmpty}>
              <Ionicons name="car-outline" size={40} color={colors.textLight} />
              <Text style={styles.shippingModalEmptyText}>{t('orders.form.noShippingMethods')}</Text>
              <TouchableOpacity onPress={() => { setShowShippingMethodModal(false); navigateTo('shipping-methods'); }}>
                <Text style={styles.shippingModalEmptyLink}>Set up shipping methods →</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <FlatList
              data={shippingMethods}
              keyExtractor={(item) => item._id || item.id}
              contentContainerStyle={{ paddingBottom: spacing.xl }}
              renderItem={({ item }) => (
                <TouchableOpacity
                  style={[
                    styles.shippingMethodItem,
                    formData.shippingMethodId === (item._id || item.id) && styles.shippingMethodItemSelected,
                  ]}
                  onPress={() => handleSelectShippingMethod(item)}
                >
                  <View style={styles.shippingMethodItemLeft}>
                    <Ionicons
                      name={item.type === 'local_pickup' ? 'storefront-outline' : item.type === 'free' ? 'gift-outline' : item.type === 'external_provider' ? 'airplane-outline' : item.type === 'online_delivery' ? 'globe-outline' : 'car-outline'}
                      size={20}
                      color={formData.shippingMethodId === (item._id || item.id) ? colors.primary : colors.textLight}
                    />
                    <View style={[rtl.marginStart(spacing.sm), { flex: 1 }]}>
                      <View style={{ flexDirection: rtl.rowDirection, alignItems: 'center', gap: spacing.xs, flexWrap: 'wrap' }}>
                        <Text style={styles.shippingMethodItemName}>{item.name}</Text>
                        {item.expressOption?.enabled && (
                          <View style={styles.expressBadge}>
                            <Ionicons name="flash-outline" size={10} color={colors.white} />
                            <Text style={styles.expressBadgeText}>{t('orders.form.express')}</Text>
                          </View>
                        )}
                      </View>
                      {item.providerName && (
                        <Text style={styles.shippingMethodItemSub}>{item.providerName}</Text>
                      )}
                      {item.estimatedDays && (
                        <Text style={styles.shippingMethodItemSub}>
                          {item.estimatedDays.min}–{item.estimatedDays.max} days
                        </Text>
                      )}
                      {item.type === 'free' && item.minOrderAmount > 0 && (
                        <Text style={styles.shippingMethodItemSub}>
                          Free above {formData.currency} {item.minOrderAmount.toFixed(2)}
                          {item.belowMinCost > 0 ? `, else ${formData.currency} ${item.belowMinCost.toFixed(2)}` : ''}
                        </Text>
                      )}
                    </View>
                  </View>
                  <View style={{ alignItems: 'flex-end' }}>
                    <Text style={styles.shippingMethodItemPrice}>
                      {(() => {
                        const subtotal = formData.subtotal || 0;
                        const effectivePrice = item.type === 'free' && item.minOrderAmount && subtotal < item.minOrderAmount
                          ? (item.belowMinCost ?? 0)
                          : (item.price ?? 0);
                        return effectivePrice === 0 ? t('orders.form.free') : `${formData.currency} ${effectivePrice.toFixed(2)}`;
                      })()}
                    </Text>
                    {item.expressOption?.enabled && (
                      <Text style={styles.shippingMethodItemExpressHint}>
                        +{formData.currency} {(item.expressOption.extraCost || 0).toFixed(2)} express
                      </Text>
                    )}
                  </View>
                </TouchableOpacity>
              )}
            />
          )}
        </View>
          </View>
        </View>
      </Modal>
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  customFieldsSection: {
    marginTop: spacing.lg,
    padding: spacing.md,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.border as string,
    backgroundColor: colors.background.surface as string,
  },
  customFieldsSectionTitle: {
    fontSize: typography.fontSize.base,
    fontWeight: '600' as const,
    color: colors.text.primary as string,
    marginBottom: spacing.md,
  },
  container: {
    flex: 1,
    backgroundColor: colors.lightGray,
    ...(Platform.OS === 'web' && {
      width: '100%',
      maxWidth: '100%',
    }),
  },
  scrollView: {
    flex: 1,
  },
  contentContainer: {
    padding: spacing.xs,
    paddingTop: spacing.xs,
    paddingBottom: spacing.lg,
    ...(Platform.OS === 'web' && {
      zIndex: 0,
      position: 'relative' as any,
    }),
  },
  contentContainerMobile: {
    paddingBottom: 100,
  },
  contentContainerWeb: {
    paddingBottom: spacing.sm,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: spacing.md,
    fontSize: typography.fontSize.base,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xl,
  },
  errorText: {
    fontSize: typography.fontSize.base,
    color: colors.error,
    fontFamily: typography.fontFamily.primary,
    textAlign: 'center',
  },
  section: {
    ...(Platform.OS === 'web' && {
      zIndex: 1,
      position: 'relative' as any,
      overflow: 'visible' as any,
    }),
    marginBottom: spacing.sm,
    paddingHorizontal: spacing.md,
    paddingTop: spacing.sm,
    paddingBottom: spacing.md,
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    ...(Platform.OS !== 'web' && {
      zIndex: 1,
    }),
  },
  sectionWithDropdown: {
    ...(Platform.OS === 'web' && {
      zIndex: 99999,
      position: 'relative' as any,
      overflow: 'visible' as any,
    }),
    ...(Platform.OS !== 'web' && {
      zIndex: 99999,
      elevation: 50,
    }),
  },
  sectionTitle: {
    fontSize: typography.fontSize.xs,
    fontWeight: '600',
    color: colors.textLight,
    marginBottom: 6,
    fontFamily: typography.fontFamily.semibold,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    flexShrink: 1,
  },
  inlineStatusRow: {
    flexDirection: 'row',
    gap: spacing.sm,
    ...(Platform.OS === 'web' && {
      overflow: 'visible' as any,
    }),
  },
  inlineStatusColumn: {
    flex: 1,
    minWidth: 0,
    ...(Platform.OS === 'web' && {
      position: 'relative' as any,
      overflow: 'visible' as any,
    }),
  },
  statusDropdown: {
    flex: 1,
  },
  statusDropdownWrapper: {
    ...(Platform.OS === 'web' && {
      zIndex: 10000,
      position: 'relative' as any,
      overflow: 'visible' as any,
    }),
    ...(Platform.OS !== 'web' && {
      elevation: 20,
    }),
  },
  statusDropdownWrapperOpen: {
    ...(Platform.OS === 'web' && {
      zIndex: 99999,
      position: 'relative' as any,
      overflow: 'visible' as any,
    }),
    ...(Platform.OS !== 'web' && {
      elevation: 50,
    }),
  },
  statusButton: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
  },
  statusButtonActive: {
    borderWidth: 2,
  },
  statusButtonText: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.xs,
  },
  itemRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    paddingVertical: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: withAlpha(colors.border, 0.38),
  },
  itemInfo: {
    flex: 1,
  },
  itemName: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
  },
  itemDetails: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    marginTop: spacing.xs / 2,
  },
  itemQuantityControls: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginTop: 4,
  },
  quantityButton: {
    padding: 2,
  },
  quantityInput: {
    width: 40,
    textAlign: 'center',
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
    color: colors.textDark,
    fontFamily: typography.fontFamily.medium,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: designTokens.borderRadius.sm,
    paddingHorizontal: 4,
    paddingVertical: 2,
  },
  itemUnitPrice: {
    fontSize: typography.fontSize.xs,
    color: colors.textLight,
    marginLeft: 2,
  },
  itemRight: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.xs,
    paddingTop: 2,
  },
  itemTotal: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.semibold,
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
  },
  removeItemButton: {
    padding: spacing.xs / 2,
  },
  addItemButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: designTokens.borderRadius.md,
    flexShrink: 0,
    borderWidth: 1,
    borderColor: colors.primary,
  },
  addItemButtonText: {
    fontSize: typography.fontSize.xs,
    color: colors.primary,
    fontFamily: typography.fontFamily.medium,
  },
  emptyItemsContainer: {
    paddingVertical: spacing.lg,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyItemsText: {
    fontSize: typography.fontSize.base,
    color: colors.textSecondary,
    marginBottom: spacing.md,
    fontFamily: typography.fontFamily.regular,
  },
  addItemButtonLarge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.primary,
    backgroundColor: withAlpha(colors.primary, 0.06),
  },
  addItemButtonTextLarge: {
    fontSize: typography.fontSize.base,
    color: colors.primary,
    fontFamily: typography.fontFamily.medium,
  },
  promoRow: {
    marginBottom: spacing.xs,
  },
  promoInputWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    backgroundColor: colors.background.surface,
  },
  promoInput: {
    flex: 1,
    fontSize: typography.fontSize.sm,
    color: colors.text.primary,
    fontFamily: typography.fontFamily.regular,
    paddingVertical: 2,
  },
  applyBtnText: {
    fontSize: typography.fontSize.sm,
    color: colors.primary,
    fontWeight: '600',
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: spacing.xs,
  },
  finalTotalRow: {
    marginTop: spacing.xs,
    paddingTop: spacing.sm,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  totalLabel: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    fontFamily: typography.fontFamily.regular,
  },
  totalValue: {
    fontSize: typography.fontSize.sm,
    color: colors.textDark,
    fontFamily: typography.fontFamily.medium,
  },
  totalValueFree: {
    color: colors.success || '#50C878',
  },
  thresholdWarning: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginTop: spacing.xs,
    paddingHorizontal: spacing.sm,
    paddingVertical: 4,
    backgroundColor: 'rgba(255, 149, 0, 0.12)',
    borderRadius: designTokens.borderRadius.sm,
  },
  thresholdWarningText: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.regular,
    color: '#FF9500',
    flex: 1,
  },
  thresholdHint: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.regular,
    color: '#FF9500',
    marginTop: 2,
  },
  shippingMethodError: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.regular,
    color: colors.error,
    marginTop: spacing.xs,
  },
  finalTotalLabel: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.bold,
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
  },
  finalTotalValue: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.bold,
    color: colors.primary,
    fontFamily: typography.fontFamily.bold,
  },
  quotationBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    backgroundColor: withAlpha(colors.primary, 0.07),
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: withAlpha(colors.primary, 0.19),
    padding: spacing.sm,
    marginBottom: spacing.md,
  },
  quotationBannerText: {
    flex: 1,
    fontSize: typography.fontSize.sm,
    color: colors.primary,
    fontFamily: typography.fontFamily.medium,
  },
  customerSection: {
    overflow: 'visible' as any,
    zIndex: 99999,
    ...(Platform.OS !== 'web' && {
      elevation: 50,
    }),
  },
  contactSelectorContainer: {
    zIndex: 99999,
    position: 'relative' as any,
    overflow: 'visible' as any,
    ...(Platform.OS === 'web' && {
      isolation: 'isolate' as any,
    }),
    ...(Platform.OS !== 'web' && {
      elevation: 50,
    }),
  },
  contactCardWrapper: {
    borderRadius: designTokens.borderRadius.lg,
    backgroundColor: colors.background.surface,
    borderWidth: 1.5,
    borderColor: colors.border,
    overflow: 'hidden',
    ...designTokens.shadows.sm,
  },
  contactSelector: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: spacing.md,
    minHeight: 64,
  },
  contactSelectorAvatar: {
    position: 'relative',
    marginRight: spacing.md,
  },
  contactSelectorAvatarImage: {
    width: 48,
    height: 48,
    borderRadius: 24,
    borderWidth: 2,
    borderColor: colors.background.surface,
  },
  contactSelectorAvatarPlaceholder: {
    backgroundColor: withAlpha(colors.primary, 0.13),
    justifyContent: 'center',
    alignItems: 'center',
  },
  contactSelectorAvatarText: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.bold,
    color: colors.primary,
    fontFamily: typography.fontFamily.bold,
  },
  contactSelectorBadge: {
    position: 'absolute',
    bottom: -2,
    right: -2,
    backgroundColor: colors.background.surface,
    borderRadius: 10,
    padding: 1,
    ...designTokens.shadows.sm,
  },
  contactSelectorContent: {
    flex: 1,
    marginRight: spacing.sm,
  },
  contactSelectorName: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.semibold,
    color: colors.textDark,
    marginBottom: 2,
    fontFamily: typography.fontFamily.semibold,
  },
  contactSelectorPhone: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    fontFamily: typography.fontFamily.regular,
  },
  contactSelectorEmpty: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  contactSelectorIcon: {
    marginRight: spacing.xs,
  },
  contactSelectorPlaceholder: {
    fontSize: typography.fontSize.base,
    color: colors.textSecondary,
    fontFamily: typography.fontFamily.regular,
  },
  contactSelectorChevron: {
    marginLeft: spacing.xs,
  },
  contactDetailsContainer: {
    overflow: 'hidden',
    borderTopWidth: 1,
    borderTopColor: withAlpha(colors.border, 0.25),
    backgroundColor: withAlpha(colors.lightGray, 0.25),
  },
  contactDetailsContent: {
    padding: spacing.md,
    paddingTop: spacing.sm,
  },
  contactDetailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.sm,
    gap: spacing.sm,
  },
  contactDetailText: {
    fontSize: typography.fontSize.sm,
    color: colors.text.primary,
    flex: 1,
    fontFamily: typography.fontFamily.regular,
  },
  slidingDropdown: {
    overflow: 'hidden',
    marginTop: spacing.xs,
    borderRadius: designTokens.borderRadius.lg,
    backgroundColor: colors.background.surface,
    borderWidth: 1.5,
    borderColor: colors.border,
    ...designTokens.shadows.md,
  },
  slidingDropdownContent: {
    maxHeight: 400,
  },
  contactDropdownSelected: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  contactDropdownText: {
    marginLeft: spacing.sm,
    flex: 1,
  },
  contactDropdownName: {
    fontSize: typography.fontSize.base,
    color: colors.text.primary,
    fontFamily: typography.fontFamily.medium,
  },
  contactDropdownPhone: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    marginTop: 2,
  },
  contactDropdownPlaceholder: {
    fontSize: typography.fontSize.base,
    color: colors.textSecondary,
    flex: 1,
  },
  contactAvatarContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    overflow: 'hidden',
  },
  contactAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
  },
  contactAvatarPlaceholder: {
    backgroundColor: withAlpha(colors.primary, 0.13),
    justifyContent: 'center',
    alignItems: 'center',
  },
  contactAvatarText: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.bold,
    color: colors.primary,
    fontFamily: typography.fontFamily.bold,
  },
  contactInfoCard: {
    marginTop: spacing.md,
    padding: spacing.lg,
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.lg,
    borderWidth: 1.5,
    borderColor: withAlpha(colors.primary, 0.13),
    ...designTokens.shadows.md,
  },
  contactInfoHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  contactInfoAvatarContainer: {
    marginRight: spacing.md,
    position: 'relative',
  },
  contactInfoAvatar: {
    width: 64,
    height: 64,
    borderRadius: 32,
    borderWidth: 3,
    borderColor: colors.background.surface,
  },
  contactInfoBadge: {
    position: 'absolute',
    bottom: -2,
    right: -2,
    backgroundColor: colors.background.surface,
    borderRadius: 12,
    padding: 2,
    ...designTokens.shadows.sm,
  },
  contactInfoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: spacing.xs,
    gap: spacing.xs,
  },
  contactInfoAvatarPlaceholder: {
    backgroundColor: withAlpha(colors.primary, 0.13),
    justifyContent: 'center',
    alignItems: 'center',
  },
  contactInfoAvatarText: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.bold,
    color: colors.primary,
    fontFamily: typography.fontFamily.bold,
  },
  contactInfoDetails: {
    flex: 1,
  },
  contactInfoName: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.bold,
    color: colors.textDark,
    marginBottom: spacing.xs / 2,
    fontFamily: typography.fontFamily.bold,
  },
  contactInfoPhone: {
    fontSize: typography.fontSize.base,
    color: colors.text.primary,
    flex: 1,
    fontFamily: typography.fontFamily.regular,
  },
  contactInfoEmail: {
    fontSize: typography.fontSize.base,
    color: colors.text.primary,
    flex: 1,
    fontFamily: typography.fontFamily.regular,
  },
  contactInfoAddress: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    flex: 1,
    fontFamily: typography.fontFamily.regular,
  },
  modalOverlay: {
    flex: 1,
    ...overlayBackdropStyle(),
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.lg,
    ...(Platform.OS === 'web' && {
      backdropFilter: 'blur(8px)',
      WebkitBackdropFilter: 'blur(8px)',
    } as any),
  },
  contactDropdownModal: {
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.xl,
    width: '100%',
    maxWidth: 520,
    maxHeight: '85%',
    overflow: 'hidden',
    ...designTokens.shadows.xl,
  },
  contactDropdownHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  contactDropdownTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.bold,
    color: colors.textDark,
    fontFamily: typography.fontFamily.bold,
  },
  contactDropdownClose: {
    padding: spacing.xs,
  },
  contactSearchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    margin: spacing.md,
    paddingHorizontal: spacing.sm,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: colors.lightGray,
  },
  contactSearchIcon: {
    marginRight: spacing.xs,
  },
  contactSearchInput: {
    flex: 1,
    fontSize: typography.fontSize.base,
    color: colors.text.primary,
    paddingVertical: spacing.xs,
    fontFamily: typography.fontFamily.regular,
  },
  addContactOption: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing.md,
    marginHorizontal: spacing.md,
    marginTop: spacing.xs,
    marginBottom: spacing.sm,
    borderRadius: designTokens.borderRadius.lg,
    backgroundColor: withAlpha(colors.primary, 0.03),
    borderWidth: 1.5,
    borderColor: withAlpha(colors.primary, 0.13),
    borderStyle: 'dashed',
  },
  addContactIconContainer: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: spacing.sm,
  },
  addContactText: {
    flex: 1,
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.semibold,
    color: colors.primary,
    fontFamily: typography.fontFamily.semibold,
  },
  contactList: {
    maxHeight: 400,
  },
  contactOption: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing.md,
    paddingHorizontal: spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: withAlpha(colors.border, 0.25),
  },
  contactOptionAvatar: {
    marginRight: spacing.md,
  },
  contactOptionAvatarImage: {
    width: 52,
    height: 52,
    borderRadius: 26,
    borderWidth: 2,
    borderColor: colors.background.surface,
  },
  contactOptionAvatarPlaceholder: {
    backgroundColor: withAlpha(colors.primary, 0.13),
    justifyContent: 'center',
    alignItems: 'center',
  },
  contactOptionAvatarText: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.bold,
    color: colors.primary,
    fontFamily: typography.fontFamily.bold,
  },
  contactOptionInfo: {
    flex: 1,
  },
  contactOptionName: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    color: colors.textDark,
    marginBottom: spacing.xs / 2,
    fontFamily: typography.fontFamily.medium,
  },
  contactOptionPhone: {
    fontSize: typography.fontSize.sm,
    color: colors.text.primary,
    marginBottom: spacing.xs / 2,
  },
  contactOptionEmail: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
  },
  contactEmpty: {
    padding: spacing.xl * 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  contactEmptyText: {
    fontSize: typography.fontSize.base,
    color: colors.textSecondary,
    fontFamily: typography.fontFamily.regular,
    textAlign: 'center',
  },
  inputError: {
    borderColor: colors.error,
  },
  errorText: {
    fontSize: typography.fontSize.sm,
    color: colors.error,
    marginTop: spacing.xs / 2,
    fontFamily: typography.fontFamily.regular,
  },
  inputContainer: {
    marginBottom: spacing.sm,
  },
  inputLabel: {
    fontSize: typography.fontSize.xs,
    fontWeight: typography.fontWeight.semibold,
    color: colors.textLight,
    marginBottom: 4,
    fontFamily: typography.fontFamily.medium,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  locationButton: {
    backgroundColor: colors.lightGray,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: designTokens.borderRadius.md,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
  },
  locationInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  locationTextContainer: {
    flex: 1,
    minWidth: 0,
  },
  locationAddress: {
    fontSize: typography.fontSize.base,
    color: colors.text.primary,
    marginBottom: spacing.xs / 2,
    fontFamily: typography.fontFamily.regular,
  },
  locationCoordinates: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    fontFamily: typography.fontFamily.regular,
  },
  locationPlaceholder: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  locationPlaceholderText: {
    fontSize: typography.fontSize.base,
    color: colors.textLight,
    flex: 1,
    fontFamily: typography.fontFamily.regular,
  },
  itemNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: spacing.xs,
  },
  itemTypeBadge: {
    paddingHorizontal: spacing.xs,
    paddingVertical: 2,
    borderRadius: designTokens.borderRadius.sm,
  },
  itemTypeBadgeText: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.medium,
  },
  itemBrand: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    fontFamily: typography.fontFamily.regular,
    marginTop: 2,
  },
  itemVendorRow: { alignItems: 'center', gap: 4, flexWrap: 'wrap', marginTop: 4 },
  itemVendorChip: { paddingVertical: 2, paddingHorizontal: 8, borderRadius: 999, borderWidth: 1, borderColor: colors.border, maxWidth: 160 },
  itemVendorChipActive: { borderColor: colors.primary, backgroundColor: withAlpha(colors.primary as string, 0.1) },
  itemVendorText: { fontSize: typography.fontSize.xs, color: colors.textSecondary, fontFamily: typography.fontFamily.regular },
  itemVendorTextActive: { color: colors.primary, fontFamily: typography.fontFamily.semibold },
  addonsSection: {
    marginTop: spacing.xs,
  },
  addonsSectionLabel: {
    fontSize: typography.fontSize.xs,
    color: colors.textLight,
    fontFamily: typography.fontFamily.medium,
    marginBottom: spacing.xs / 2,
  },
  addonsList: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.xs / 2,
  },
  addonChip: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.xs,
    paddingVertical: 4,
    borderRadius: designTokens.borderRadius.sm,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.lightGray,
    gap: 4,
  },
  addonChipSelected: {
    borderColor: colors.primary,
    backgroundColor: withAlpha(colors.primary, 0.08),
  },
  addonChipInner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
  },
  addonQty: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.bold,
    color: colors.primary,
    minWidth: 14,
    textAlign: 'center',
  },
  addonChipText: {
    fontSize: typography.fontSize.xs,
    color: colors.textSecondary,
    fontFamily: typography.fontFamily.regular,
  },
  addonChipTextSelected: {
    fontSize: typography.fontSize.xs,
    color: colors.primary,
    fontFamily: typography.fontFamily.medium,
    marginHorizontal: 2,
  },
  addonRemove: {
    marginLeft: 2,
  },
  addonReadOnly: {
    fontSize: typography.fontSize.xs,
    color: colors.textSecondary,
    fontFamily: typography.fontFamily.regular,
    marginTop: 2,
  },

  // Shipping method selector
  shippingMethodPicker: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: colors.lightGray,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    gap: spacing.xs,
  },
  shippingMethodPlaceholder: {
    flex: 1,
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.regular,
    color: colors.textLight,
  },
  shippingMethodSelected: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: withAlpha(colors.primary, 0.25),
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: withAlpha(colors.primary, 0.03),
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
  },
  shippingMethodInfo: {
    flex: 1,
  },
  shippingMethodName: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
    color: colors.text.primary,
  },
  shippingMethodPrice: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.regular,
    color: colors.textSecondary,
    marginTop: 2,
  },
  shippingMethodActions: {
    flexDirection: 'row',
    gap: spacing.xs,
  },
  shippingMethodBtn: {
    padding: spacing.xs,
  },

  // Shipping method modal
  shippingModalNativeContainer: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  shippingModalNativeBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
  },
  shippingModalWebOverlay: {
    flex: 1,
    position: 'relative',
  },
  shippingModalBackdrop: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: 'rgba(0,0,0,0.4)',
    zIndex: 1,
  },
  shippingModalWebContainer: {
    position: 'absolute',
    top: 180,
    left: 355,
    right: 355,
    bottom: 0,
    zIndex: 2,
    justifyContent: 'flex-end',
  },
  shippingModalSheet: {
    backgroundColor: colors.background.surface,
    borderTopLeftRadius: designTokens.borderRadius.xl,
    borderTopRightRadius: designTokens.borderRadius.xl,
    maxHeight: '70%',
    paddingTop: spacing.md,
  },
  shippingModalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.lg,
    paddingBottom: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  shippingModalTitle: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.semiBold,
    color: colors.text.primary,
  },
  shippingModalEmpty: {
    alignItems: 'center',
    paddingVertical: spacing.xxl,
    gap: spacing.sm,
  },
  shippingModalEmptyText: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.regular,
    color: colors.textLight,
  },
  shippingModalEmptyLink: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
    color: colors.primary,
    marginTop: spacing.xs,
  },
  shippingMethodItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  shippingMethodItemSelected: {
    backgroundColor: colors.primaryLight || '#EEF2FF',
  },
  shippingMethodItemLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  shippingMethodItemName: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
    color: colors.text.primary,
  },
  shippingMethodItemSub: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.regular,
    color: colors.textSecondary,
    marginTop: 2,
  },
  shippingMethodItemPrice: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.semiBold,
    color: colors.primary,
    marginLeft: spacing.sm,
  },
  shippingMethodItemExpressHint: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.regular,
    color: colors.textSecondary,
    marginLeft: spacing.sm,
    marginTop: 2,
  },
  expressBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FF9500',
    borderRadius: 4,
    paddingHorizontal: 4,
    paddingVertical: 1,
    gap: 2,
  },
  expressBadgeText: {
    fontSize: 9,
    fontFamily: typography.fontFamily.medium,
    color: colors.white,
  },
  expressToggle: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginTop: spacing.xs,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.primary,
    backgroundColor: colors.background.surface,
    alignSelf: 'flex-start',
  },
  expressToggleActive: {
    backgroundColor: colors.primary,
  },
  expressToggleText: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.medium,
    color: colors.primary,
  },
  expressToggleTextActive: {
    color: colors.white,
  },
});
